/*
 * lib/timerStore.js — the ONE place timer slots are written.
 * One-click timers and the Timers panel (docs/specs/timers-one-click-spec.md,
 * Track C, with the 2026-10-14 review-panel amendments 1-3, 5, 7, 8).
 *
 * The slot IS the clock.  Every surface (form, panel, chip, bar, page buttons,
 * tray / shortcut handlers) reads elapsed time from the slot and changes it
 * through this module, as a synchronous read-fresh → pure transition → write,
 * followed by a same-window TIMER_SLOT_EVENT.  No locks, no compare-and-set:
 * transitions are idempotent and deterministic (one T per click), the read-time
 * implicit pause (effectiveElapsed) means a lost write can never inflate time,
 * the debounced reconciler converges every window on one running timer, the
 * per-window clobber journal re-applies this window's own writes when another
 * renderer's stale read overwrites them, and the save claim + client_ref (tid)
 * stop a second save.
 *
 * ═══════════════════════════ FROZEN API (Track C) ═══════════════════════════
 * Other tracks build against exactly this.  Add, never change.
 *
 * Common shapes
 *   uid      String user id FROM THE TOKEN (tokenUid()), never AuthContext.
 *            Every method taking { uid } uses tokenUid() when uid is omitted
 *            (undefined); null/'' means "nobody signed in" → refused / empty.
 *   Result   { ok: true, key, changed: boolean, slot?, ... }
 *          | { ok: false, reason, message, ... }
 *            reason ∈ 'storage' 'invalid' 'missing' 'malformed' 'foreign'
 *            'claimed' 'held' 'needs-adopt' 'needs-confirm' 'cancelled'
 *            'resume-locked' 'post-uncertain' 'ticking' 'no-user' 'no-matter'
 *            'no-description' 'no-time' 'too-long' 'matter-mismatch'
 *            'confirm-owner' 'not-mine' 'exists'.
 *            message is the user-facing line (MESSAGES).  A write that hit a
 *            storage exception returns { ok:false, reason:'storage' } — the
 *            surface shows MESSAGES.storage persistently and starts nothing.
 *   confirm  async (req) => boolean, where req = { kind: 'start-anyway' |
 *            'adopt', message, confirmLabel, danger: true, key|keys, label }.
 *            Surfaces pass: (req) => ask({ type: 'confirm', danger: true,
 *            message: req.message, confirmLabel: req.confirmLabel }).
 *   Row      (listMyTimers / getTimer) { key, slot, cls, probe, v2, ticking,
 *            implicitlyPaused, elapsed, label, matterId, matterTitle,
 *            hasMatter, description, isResume, resumeEntryId, onBehalfOf,
 *            claimed, postUncertain, uidAssumed, owner, ownerState, heldHere,
 *            heldElsewhere, futureStart, longRunning, fromBeforeUpdate,
 *            hidden, mine, lastActivity }
 *            cls ∈ 'v2' | 'v1-held' (an old-bundle window holds it: reach it
 *            only by command) | 'adopt-silently' (v1, no old holder can exist)
 *            | 'v1-unknown' (v1, an old tab/window may tick it: confirm before
 *            adopting; `probe` = 'timer-resume-<id>' when isPopupOpen can
 *            settle it).  `ticking` is EFFECTIVE (false when a later-started
 *            timer truncates it — implicitlyPaused).  `elapsed` = effective
 *            seconds now.  Sorted ticking first, then last activity, then key.
 *   HeldInfo { key, cmd, pk, label } — a timer an old-bundle window holds was
 *            sent a command; pass result.held to timerWindows.awaitHeldPauses
 *            so silence is reported ("Pause "<label>" in its window").
 *
 * Module exports
 *   TIMER_SLOT_EVENT ('lextrack-timer-slot', detail { key, action }),
 *   BROADCAST_KEY, CLAIM_FRESH_MS (60 s), JOURNAL_MS (5 s),
 *   RECONCILE_DEBOUNCE_MS (250), FUTURE_START_MS (60 s), MAX_ENTRY_HOURS (24),
 *   LONG_RUNNING_SECONDS (24 h), POST_FIELDS, PUT_FIELDS, BILLING_STATUSES,
 *   MESSAGES, billableHoursFromSeconds (re-export), firmDateIso (re-export)
 *   createTimerStore({ storage, now, dispatch, subscribe, broadcast, getUid,
 *     env }) → store.  storage: a Storage or () => Storage; now: () => ms;
 *     dispatch(detail): same-window slot event; subscribe(handler) →
 *     unsubscribe: storage events for the clobber journal (attached lazily on
 *     the first write); broadcast(type, payload): the saved broadcast; env:
 *     { hasTimerBar(), isDesktopMain(), isPopupOpen(pk)→Promise<bool>|null,
 *     firmTz(), firmDefaultBillingStatus(), sendCommand(key, action, opts),
 *     tabId() }.
 *   timerStore (also the default export) — the instance bound to
 *     window.localStorage, Date.now, window 'storage' events, the
 *     TIMER_SLOT_EVENT CustomEvent and the real broadcast.
 *   tokenUid() → String|null
 *   broadcastTimerEvent(type, payload) — 'lextrack_timer_broadcast' + the
 *     same-window 'timerEntrySaved' event (what useTimerEntrySaved hears).
 *   parseStoredSlot(raw) → { slot } | { malformed: true } | null
 *   effectiveElapsed(entry, entries, now) → seconds (pure; entries =
 *     [{ key, slot, hidden? }])
 *   planSingleRunning(entries, uid, now) → [{ key, at }] (pure reconciler plan)
 *   slotSaveHours(slot, seconds?) → hours (hours_typed when paused and > 0,
 *     else ceil to 0.1 of seconds — billableHoursFromSeconds)
 *   buildPostBody(slot, { hours, postImmediately }) → whitelisted POST body:
 *     matter_id, date, hours, rate (only when set), description,
 *     billing_status, activity_code, user_id (only from slot.user_id, only
 *     when on behalf of someone else), matter_phase_id, post_immediately,
 *     client_ref (= tid), start_time (slotStartTime, or null).  Never uid /
 *     v / sig / claim / markers.
 *   buildPutBody(slot) → resume PUT body (date, rate when set, description,
 *     billing_status, activity_code, matter_phase_id; start_time only when
 *     start_time_touched) — no matter_id, user_id, status or billed; hours
 *     go as saveResumedEntry's hoursStr.
 *   slotStartTime(slot, tz?) → 'HH:MM' | '' — the entry's start time (added
 *     2026-10-15, firm-local time of day): a typed or resume-seeded
 *     slot.start_time, else '' on a resume slot, else the firm-local time
 *     of day of created_at — the timer's FIRST start (a resume moves
 *     startTime, never created_at).
 *   replayDiff(slot, row, { hours }) → { differs, fields: [names] } — the
 *     whitelisted fields (hours, description, rate when rate_touched,
 *     billing_status, activity_code, matter_phase_id, date, start_time when
 *     the row carries it) where a replayed server row differs from the slot
 *     (amendment 7).
 *   postUncertainty(err) → true | false | undefined (amendment 8), the
 *     value for releaseClaim's `uncertain`: true = no definitive answer (no
 *     response, network error, timeout, 5xx — the POST may have landed:
 *     stamp post_uncertain); false = definitive (2xx, 400, 404, 409, 422 —
 *     the route answers after its client_ref lookup: clear it); undefined =
 *     refused before that lookup (401, 403, 408, 429, other 4xx — says
 *     nothing about an earlier attempt: leave post_uncertain as it is).
 *   isUncertainPostError(err) → postUncertainty(err) !== false (the
 *     conservative boolean: only a definitive answer is "certain").  Prefer
 *     releaseClaim(key, claimId, { error: err }).
 *   saveConflictMessage(responseData) → the line for a 409 on POST:
 *     MESSAGES.clientRefDeleted for code 'client_ref_deleted' (no
 *     existing_entry_id; a later Save records it again), else
 *     MESSAGES.alreadySavedOther(existing_entry_id) — the generic 'Already
 *     saved as another time entry' when the server left the id out (it sends
 *     it only to a caller who could open that entry).
 *   clampCommandAt(slot, at, now, action) → T for a bar command's cmd.at.
 *   FIRM_DEFAULT_BILLING_KEY ('lextrack_firm_default_billing_status') — the
 *     firm's default billing status as Layout last loaded it from
 *     /api/settings, for windows that never load settings (the floating
 *     bar).  rememberFirmDefaultBillingStatus(value, storage?) → boolean
 *     writes it (only a BILLING_STATUSES value, only when it differs);
 *     readFirmDefaultBillingStatus(storage?) → value | null.  The default
 *     env.firmDefaultBillingStatus is window.__lextrackDefaultBillingStatus,
 *     else this key, else 'billable' — so a bar start and a main-window start
 *     get the same default, and setMatter / blockMatter re-derive with it.
 *   REMOVED_PREFIX ('lextrack_timer_removed:'), REMOVED_NOTE_MS (5 min) — the
 *     per-uid removal note 'lextrack_timer_removed:<uid>' = [{ key, tid, why,
 *     at }]: why a slot vanished, written BEFORE the slot is removed so a form
 *     open on it (any window) can say so.  No matter name, no fields.
 *
 * Store methods (sync unless marked async)
 *   readSlot(key) → slot object | null (missing, malformed or not a timer)
 *   getTimer(key, { uid, now }) → Row | null — any visibility, `mine` flag
 *   classify(key, { now }) → { cls, probe } | null
 *   listMyTimers(uid, { now }) → Row[] — my visible timers: v2 slots whose
 *     uid is mine (hidden only when ran===false && !dirty and no window holds
 *     them; never for age), v1 slots by today's visibleSlotsForUser +
 *     isAbandonedSlot rules.  Malformed slots are skipped, never thrown on.
 *   effectiveElapsedSeconds(key, { now }) → seconds
 *   async startNewTimer({ uid, fields, at, seedSeconds, key, confirm })
 *     → Result + { at, paused: [keys], held: [HeldInfo], adopted: [keys] }.
 *     The one-running rule: pauses my other ticking v2 timers at the same T
 *     (each at min(T, its own cut)), adopts+pauses adopt-silently ones at T
 *     (every second old code counted), sends 'pause' to v1-held ones (see
 *     timerWindows.awaitHeldPauses), and asks confirm('start-anyway') first
 *     when a v1-unknown timer of mine ticks (Cancel → nothing written).
 *     T = at (default now), clamped to [the last instant any OTHER timer of
 *     mine counted — a ticking one's startTime, a paused one's pausedAt —,
 *     now]: a T in the past never pauses a timer before its own start and
 *     never lets two timers count the same second.  result.at = that T.
 *     fields: matter_id, matter_title, billing_type, description,
 *     activity_code, billing_status, billing_touched, rate, rate_touched,
 *     user_id, matter_phase_id, date, start_time + start_time_touched (kept
 *     only when start_time_touched is true).  billing_touched / rate_touched are
 *     true ONLY when passed true (a prefill or the firm default is not a
 *     choice).  key: optional fresh 'lextrack_timer:<uuid>' (default
 *     newSlotKey()).  Fires TIMER_SLOT_EVENT per write.
 *   async startOrResume(key, { uid, at, confirm }) → same Result shape.
 *     T = at clamped to [this slot's pausedAt, now], then the same clamp as
 *     startNewTimer.
 *     Paused → resumes at T and pauses my others at T; already ticking → no-op
 *     (changed:false); implicitly paused → materialises the cut, resumes;
 *     claimed → refused; v1-held → sends 'resume' (result.viaCommand + held);
 *     adopt-silently / v1-unknown → adoptV1 (with confirm) first.
 *   pause(key, { uid, at }) → Result.  Idempotent; a v2 slot pauses at
 *     min(T, its cut); v1-held → 'pause' command (viaCommand, held);
 *     adopt-silently → adopts + pauses at T in one write (every second old
 *     code counted); v1-unknown → { ok:false, reason:'needs-adopt' } (call
 *     adoptV1 first).
 *     SETTLE: every transition that stops or removes a ticking slot — pause,
 *     claimForSave (incl. its refusals that pause), discard, finishSave,
 *     blockMatter, setTypedHours on an implicitly paused slot, adoptV1 when it
 *     pauses — first writes the reconciler's cuts for my other ticking v2
 *     timers (each paused at its own cut, extra TIMER_SLOT_EVENTs), so a cut
 *     never vanishes: no second counted twice, nothing resumes by itself.
 *   async toggle(key, { uid, at, confirm }) — pause if effectively ticking,
 *     else startOrResume (adopting first when needed).
 *   async pauseOrStart({ uid, at, confirm, fields }) → { ok, action:
 *     'paused' | 'started', ... } — Ctrl/Cmd+Shift+T: pause my effectively
 *     ticking timer(s) (an old-code one is adopted first, via confirm when
 *     needed; an old-window one gets the 'pause' command — see result.held),
 *     otherwise startNewTimer.
 *   seedResume(entryId, { uid, entry, seedSeconds, at, entryUpdatedAt, date })
 *     → Result + { existed } — the canonical 'lextrack_timer:resume:<id>'
 *     slot, PAUSED with ran:false.  A live slot already there is restored
 *     (existed:true, nothing written), never re-seeded.  date defaults to the
 *     entry's own date, else today in the firm zone.  start_time = the
 *     entry's ('HH:MM' or ''), start_time_touched:false.
 *   setFields(key, partial, { uid }) → Result.  Allowed: description,
 *     activity_code, billing_status, billing_touched, rate, rate_touched,
 *     matter_phase_id, date, start_time ('HH:MM[:SS]' or '' / null),
 *     start_time_touched.  billing_status implies billing_touched:true, a
 *     non-empty rate implies rate_touched:true and start_time implies
 *     start_time_touched:true unless given.  Never touches
 *     clock fields.  Missing / foreign / v1 / claimed slots → refused, nothing
 *     written.  Sets dirty:true when something changed.
 *   setMatter(key, { matter_id, matter_title, billing_type }, { uid }) →
 *     Result.  One merge: matter_id ('' or digits) + matter_title, clears
 *     matter_phase_id, re-derives billing_status unless billing_touched,
 *     clears rate to '' unless rate_touched.  Refused on resume slots
 *     ('resume-locked') and while post_uncertain ('post-uncertain').
 *   setAttorney(key, userId, { uid }) → Result.  user_id '' = self; clears
 *     rate unless rate_touched; same refusals as setMatter.
 *   setTypedHours(key, hours, { uid }) → Result.  Paused only ('ticking'
 *     otherwise; an implicitly paused timer is paused at its cut in the same
 *     write): hours_typed = hours and pausedSeconds = max(0,
 *     round((hours − 0.10) × 3600)); '' / 0 / invalid clears hours_typed and
 *     keeps the clock.
 *   claimForSave(key, { uid, formMatterId, at, ownershipConfirmed }) →
 *     { ok:true, key, claimId, slot, hours, seconds, isResume, entryId,
 *     expectedUpdatedAt, body } (body = POST or PUT body) | refusal.
 *     Refuses missing / foreign / held / v1 / fresh claim (< 60 s) /
 *     matter-mismatch WITHOUT writing.  Refuses no-matter (new entries),
 *     no-description, no-time and too-long (> 24 h) after writing the slot
 *     PAUSED at T with exact seconds (result.paused:true).  uid_assumed
 *     slots refuse 'confirm-owner' until ownershipConfirmed:true.  Success
 *     writes paused at T + claim { id, at }.
 *   releaseClaim(key, claimId, { uncertain, error }) → Result.  Drops the
 *     claim (the slot stays paused); uncertain:true stamps post_uncertain
 *     {at}, uncertain:false clears it, undefined leaves it.  `error` (the
 *     failed request's error) sets uncertain = postUncertainty(error) — the
 *     recommended call; an explicit `uncertain` wins.
 *   markReplayed(key, claimId, { entryId, entryUpdatedAt }) → Result — a
 *     replayed POST whose fields differ: the slot becomes a resume slot for
 *     that entry (claim kept, post_uncertain cleared — a replay is
 *     definitive) so the follow-up save PUTs; an untouched start time is
 *     pinned (start_time = slotStartTime, start_time_touched:true) so that
 *     PUT carries what the POST meant.
 *   finishSave(key, claimId, { broadcast = true, tid }) → Result.  Removes
 *     the slot and its owner key when the claim is ours — or when only the
 *     tid matches (this window's claimForSave remembers claimId → tid; a
 *     stale claim from another window may have replaced the id) — then
 *     broadcastTimerEvent('saved').  A slot already gone is ok.
 *   discard(key, { uid, force }) → Result.  Removes slot + owner key.
 *     Refuses claimed and v1-held; v1-unknown needs force (adopt-confirm
 *     first).  Missing → ok with changed:false.
 *   async adoptNeedsConfirm(key, { uid }) → boolean — true only when `key`
 *     is a v1-unknown slot of mine that adoptV1 would confirm (no isPopupOpen
 *     probe, or the probe did not say its window is closed).  Never writes.
 *     Discard folds the adopt question into its ONE confirm
 *     (MESSAGES.discardOldConfirm), then discard(key, { force: true }).
 *   removalNote(key, { uid, tid, now }) → { why, message } | null — why my
 *     slot `key` (that timer instance, when tid is given) vanished, from the
 *     removal note, within REMOVED_NOTE_MS.  why 'blocked' (blockMatter
 *     removed a resume timer) → MESSAGES.blockedRemoved.  Never writes.
 *   discardAllMine(uid) → { ok, removed: [keys] } — only this uid's v2
 *     slots (not claimed ones); v1 slots untouched.  Sign-out "Discard them".
 *   blockMatter(matterId, { uid, at }) → { ok, paused, cleared, removed } —
 *     matter.blocked: my v2 slots on that matter pause (each at min(T, its
 *     cut)) and lose matter_id, matter_title and phase ('No matter yet');
 *     my v2 resume slots on it are removed (each after a removal note, why
 *     'blocked' — see removalNote); claimed ones are left.  A
 *     post_uncertain slot is cleared the same way and loses post_uncertain
 *     (its retry could never reach the blocked matter; the server's
 *     client_ref lookup still answers 409 if the first POST landed).
 *   async adoptV1(key, { uid, confirm }) → Result + { adopted,
 *     pausedOnAdopt }.  Keeps the key and every field, adds v:2, sig, tid,
 *     ran:true and billing_touched:true; a uid-less slot gets the uid +
 *     uid_assumed:true; a slot with no `date` (the legacy in-page timer) gets
 *     the firm-zone day of its own start.  It never moves the clock: a
 *     TICKING slot keeps its startTime and keeps ticking — unless a
 *     later-started timer of mine ticks, which stays the running one: then
 *     the adopted slot is paused at the adoption instant with every second
 *     old code counted (pausedOnAdopt:true).  [NEEDS FIRM SIGN-OFF: the spec
 *     said only "keeps every field".]
 *     v1-held → refused; v1-unknown → isPopupOpen probe, else
 *     confirm('adopt'); re-classified after the await (an old window that
 *     took it meanwhile wins: 'held').
 *   reconcile(uid, at) → { ok, wrote: [keys] } — writes planSingleRunning:
 *     each ticking v2 loser paused at min(now, the earliest later startTime
 *     among my ticking slots, v1 or v2).  v1 slots are never written.
 *   handleStorageEvent(event) — the clobber journal; called automatically
 *     once subscribed.  Idempotent.  Re-applies only onto the same timer
 *     instance (tid), at most once per write, within JOURNAL_MS; a
 *     re-applied earlier pause keeps another window's fresh in-flight claim
 *     (never this window's own released one).
 *   billableHoursFromSeconds, buildPostBody, buildPutBody — as above;
 *   formatElapsed(seconds) → 'H:MM:SS' (timerSlots.formatBarElapsed).
 *
 * Companion modules (Track C — frozen too)
 *   lib/timerSlots.js — adds RESUME_SLOT_PREFIX, TIMER_BAR_PROTO_KEY
 *     ('lextrack_timer_bar_proto'), BAR_PROTO_VERSION (2), NO_MATTER_LABEL
 *     ('No matter yet'), isResumeSlotKey(key), resumeEntryIdOfKey(key),
 *     resumeSlotKey(entryId), newUuid(), newSlotKey(), sigOf(slot),
 *     isV2(slot), readBarProto(storage); parseOwner → { at, pk, ack, v, by,
 *     tab }; adoptableSlotKey accepts the legacy key only when v2; slotLabel
 *     falls back to 'No matter yet'.  The v1 ghost rules are unchanged.
 *   lib/timerCommands.js — COMMAND_ACTIONS frozen ['pause','resume','focus'];
 *     COMMAND_EVENT ('lextrack-timer-command'); getTabId();
 *     sendTimerCommand(key, action, { storage, now, dispatch = true }) also
 *     dispatches COMMAND_EVENT in this window; useTimerCommandListener(key,
 *     onCommand, { enabled = true }) hears both, once per id;
 *     useTimerSlotHeartbeat(key, { enabled = true, popupKey, active, by })
 *     writes { at, pk, ack, v:2, by, tab } on every platform.
 *   lib/timerWindows.js — COMMAND_ACK_MS (1500); awaitAck({ storage,
 *     storageKey, cmd, onSilence, ms, pending }) → cancel;
 *     reopenTimerWindow(key, pk); async openTimerForm(key, { storage,
 *     electronAPI, now, pending, open, sendCommand }) → { how: 'focused' |
 *     'reopened' | 'command' | 'opened' | 'legacy-focus' | 'missing' |
 *     'invalid' | 'unsupported', pk?, cmd? } ('unsupported' = not desktop:
 *     show the in-page form); awaitHeldPauses(held, { storage, onSilence,
 *     electronAPI, ms }) → cancel, onSilence({ key, label, pk, message }).
 *   hooks/useTimers.js — useTimers({ store, uid, tick = true, forceTick,
 *     reconcile = true }) → { timers, current, anyTicking, count, now, uid,
 *     refresh } (default and named export).  Every new-bundle window with a
 *     uid that should run the reconciler mounts it (Layout, pop-outs, bar);
 *     tick:false for a consumer that must not re-render every second.  The
 *     reconciler runs RECONCILE_DEBOUNCE_MS after mount, slot storage events,
 *     key-null events and this window's own TIMER_SLOT_EVENT.
 *   lib/timeEntryPosting.js — saveResumedEntry(api, id, form, hoursStr,
 *     { post, expectedUpdatedAt }) sends expected_updated_at; on a 409 it
 *     re-GETs: the entry already holds the hours AND every field the PUT
 *     sends (entryHoldsPut) → { alreadySaved: true } (post still runs), else
 *     throws code 'entry_changed' with response.data.error =
 *     ENTRY_CHANGED_MSG; the result also carries updated_at.
 *   utils/eventTime.js — firmDateIso(at = Date.now(), tz = firmTz()).
 *   lib/uiPrefs.js — "Active work" also clears lextrack_timer_owner:*,
 *     lextrack_timer_command, lextrack_timer_bar_proto,
 *     lextrack_timer_removed:* and lextrack_firm_default_billing_status
 *     (Layout writes the last one again after a clear).
 *
 * Events fired: TIMER_SLOT_EVENT after every slot write or removal (detail
 * { key, action }); 'lextrack_timer_command' storage write (+ same-window
 * COMMAND_EVENT) for v1-held pause/resume; 'lextrack_timer_broadcast' +
 * 'timerEntrySaved' from finishSave.
 * ════════════════════════════════════════════════════════════════════════
 */

import { billableHoursFromSeconds, firmTimeOfDay, normalizeStartTime } from './timeEntryPosting'
import { deriveDefaultBillingStatus, firmDefaultBillingStatus } from '../utils/billingDefaults'
import { firmDateIso, firmTz } from '../utils/eventTime'
import { hasTimerBar } from './desktopCapabilities'
import { getToken } from '../services/token'
import { getTabId, sendTimerCommand } from './timerCommands'
import {
  NO_MATTER_LABEL, decodeTokenUserId, formatBarElapsed, isAbandonedSlot, isLegacySlotKey,
  isResumeSlotKey, isTimerSlotKey, isV2, lastActivity, newSlotKey, newUuid, ownerKeyFor,
  ownerState, parseOwner, resumeEntryIdOfKey, resumeSlotKey, sigOf, slotLabel
} from './timerSlots'

export { billableHoursFromSeconds, firmDateIso }

export const TIMER_SLOT_EVENT = 'lextrack-timer-slot'
export const BROADCAST_KEY = 'lextrack_timer_broadcast'
export const CLAIM_FRESH_MS = 60 * 1000
export const JOURNAL_MS = 5000
export const RECONCILE_DEBOUNCE_MS = 250
export const FUTURE_START_MS = 60 * 1000
export const MAX_ENTRY_HOURS = 24
export const LONG_RUNNING_SECONDS = 24 * 3600
export const BILLING_STATUSES = Object.freeze(['billable', 'non-billable', 'no-charge-show', 'no-charge-hide'])
// Registered in lib/uiPrefs.js ("Active work").
export const FIRM_DEFAULT_BILLING_KEY = 'lextrack_firm_default_billing_status'
// Does NOT start with 'lextrack_timer:' (not a slot — isTimerSlotKey is false).
export const REMOVED_PREFIX = 'lextrack_timer_removed:'
export const REMOVED_NOTE_MS = 5 * 60 * 1000
export const POST_FIELDS = Object.freeze([
  'matter_id', 'date', 'hours', 'rate', 'description', 'billing_status', 'activity_code',
  'user_id', 'matter_phase_id', 'post_immediately', 'client_ref', 'start_time'
])
export const PUT_FIELDS = Object.freeze([
  'date', 'rate', 'description', 'billing_status', 'activity_code', 'matter_phase_id', 'start_time'
])

export const MESSAGES = Object.freeze({
  storage: "This browser can't store timers — time is not being recorded",
  started: 'Timer started — pick the matter any time from ⏱ Timers',
  // A one-click start that already carries its matter (the matter page's
  // '⏱ Start timer'): no "pick the matter" nudge, and no matter name either.
  startedOnMatter: 'Timer started — add the description any time from ⏱ Timers',
  noPermission: "You can't record time — ask an admin",
  invalid: "That timer can't be changed.",
  missing: 'This timer was saved or discarded in another window.',
  malformed: 'This timer record is damaged and was skipped.',
  foreign: 'This timer belongs to someone else signed in on this computer.',
  claimed: 'This timer is being saved — try again in a moment.',
  held: 'This timer is open in a window from before the update — use that window.',
  needsAdopt: 'This timer was started before the update — confirm before changing it here.',
  needsConfirm: 'Confirmation needed.',
  cancelled: 'Nothing was changed.',
  resumeLocked: 'The matter and attorney are locked while you update an existing entry.',
  postUncertain: 'Checking whether this time was already saved — click Save again',
  ticking: 'Pause the timer before typing the hours.',
  noUser: 'Sign in to record time.',
  noMatter: 'Pick a matter before saving — your time is kept',
  noDescription: 'Description is required. Add a description, then save again — your time is kept.',
  noTime: 'No time recorded yet — start the timer or type the hours.',
  tooLong: 'Single entries are capped at 24 hours — check the time; your time is kept.',
  matterMismatch: 'The matter changed in another window — check it, then save again.',
  confirmOwner: 'This timer was started before the update — confirm it is yours before saving.',
  notMine: 'This timer is being saved somewhere else.',
  exists: 'That timer already exists.',
  entryChanged: 'This entry changed since you resumed it',
  periodClosed: "That date's period is closed — change the date to save",
  clockChanged: 'Clock changed — check the time',
  discardInItsWindow: 'Discard it in its window',
  footer: 'Only one timer runs at a time in this app on this computer — the browser and the desktop app keep separate timers.',
  empty: 'No open timers. Start one — you can pick the matter later.',
  restartBar: 'Restart Omnia to finish updating the timer bar',
  noMatterLabel: NO_MATTER_LABEL,
  noDescriptionLabel: 'No description yet',
  tooLongHours: (h) => `Hours value is too large (${Number(h).toFixed(1)} hr). Single entries are capped at 24 hours — check the time; your time is kept.`,
  alreadySavedOther: (id) => (id != null && id !== '' ? `Already saved as entry #${id} on another matter` : 'Already saved as another time entry'),
  clientRefDeleted: 'This time was saved, then that entry was deleted. Click Save again to record it again, or Discard the timer.',
  longRunning: (seconds) => `Running ${Math.floor(Math.max(0, Number(seconds) || 0) / 3600)} h — check the time`,
  startAnyway: (label) => `"${label}", started before the update, may still be running in another tab or window. Pause it there first — or start anyway and both will count.`,
  adoptConfirm: (label) => `"${label}" was started before the update and may still be running in another tab or window. Use it here? If it is still open there, pause or close it there first.`,
  confirmOwnerAsk: (name) => `Save this timer as yours (${name})?`,
  heldPause: (label) => `Pause "${label}" in its window`,
  heldResume: (label) => `Resume "${label}" in its window`,
  discardConfirm: ({ elapsed, matter, isResume }) => `Discard this timer (${elapsed}, ${matter || NO_MATTER_LABEL})? ${isResume ? 'The entry keeps its saved hours.' : 'The time will not be saved.'}`,
  // Discard on a timer old code may still run elsewhere (v1-unknown): the
  // adopt question and the discard question in ONE confirm.
  discardOldConfirm: ({ elapsed, matter, isResume }) => `Discard this timer (${elapsed}, ${matter || NO_MATTER_LABEL})? It was started before the update and may still be open in another tab or window — discarding it here does not stop it there. ${isResume ? 'The entry keeps its saved hours.' : 'The time will not be saved.'}`,
  blockedRemoved: 'You no longer have access to this matter, so this timer was removed. The entry keeps its saved hours.',
  signOut: (n) => n === 1
    ? 'You have 1 open timer — it stays on this computer, paused, until you sign back in'
    : `You have ${n} open timers — they stay on this computer, paused, until you sign back in`
})

const REASON_MESSAGE = {
  storage: MESSAGES.storage,
  invalid: MESSAGES.invalid,
  missing: MESSAGES.missing,
  malformed: MESSAGES.malformed,
  foreign: MESSAGES.foreign,
  claimed: MESSAGES.claimed,
  held: MESSAGES.held,
  'needs-adopt': MESSAGES.needsAdopt,
  'needs-confirm': MESSAGES.needsConfirm,
  cancelled: MESSAGES.cancelled,
  'resume-locked': MESSAGES.resumeLocked,
  'post-uncertain': MESSAGES.postUncertain,
  ticking: MESSAGES.ticking,
  'no-user': MESSAGES.noUser,
  'no-matter': MESSAGES.noMatter,
  'no-description': MESSAGES.noDescription,
  'no-time': MESSAGES.noTime,
  'too-long': MESSAGES.tooLong,
  'matter-mismatch': MESSAGES.matterMismatch,
  'confirm-owner': MESSAGES.confirmOwner,
  'not-mine': MESSAGES.notMine,
  exists: MESSAGES.exists
}

const fail = (reason, extra = {}) => ({ ok: false, reason, message: REASON_MESSAGE[reason] || reason, ...extra })

// ── small pure helpers ───────────────────────────────────────────────────
const num = (v, d = 0) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : d
}
const str = (v) => (typeof v === 'string' ? v : v == null ? '' : String(v))
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isTicking = (slot) => !!slot && !slot.paused
const baseSeconds = (slot) => Math.max(0, Math.floor(num(slot && slot.pausedSeconds)))

// '' or a positive int4 as a string of digits; null for anything else.
function cleanMatterId (v) {
  if (v == null || v === '') return ''
  const s = String(v).trim()
  if (!/^\d{1,10}$/.test(s)) return null
  const n = Number(s)
  return n > 0 && n <= 2147483647 ? String(n) : null
}

function cleanUserId (v) {
  if (v == null || v === '') return ''
  const s = String(v).trim()
  return /^\d{1,10}$/.test(s) && Number(s) > 0 ? String(Number(s)) : null
}

// null, or a positive integer; undefined for garbage.
function cleanPhase (v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isInteger(n) && n > 0 ? n : undefined
}

function dateOnly (v) {
  if (typeof v !== 'string') return ''
  const d = v.slice(0, 10)
  return DATE_RE.test(d) ? d : ''
}

function cleanRate (v) {
  if (v == null || v === '') return ''
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? String(v).trim() : null
}

const claimIsFresh = (claim, now) =>
  !!claim && typeof claim === 'object' && Number.isFinite(Number(claim.at)) &&
  Math.abs(now - Number(claim.at)) < CLAIM_FRESH_MS

// Elapsed seconds of `slot` counted up to `until` (ms).  Amendment 2: the
// ticking part is max(0, …) on its own, so a backward clock step never eats
// paused seconds and never draws a negative time.
function rawElapsed (slot, until) {
  const base = baseSeconds(slot)
  if (!isTicking(slot)) return base
  return base + Math.max(0, Math.floor((until - num(slot.startTime)) / 1000))
}

// Is entry O "later" than entry S?  Start time, ties broken by key, so two
// timers started in the same millisecond still have exactly one winner.
function isLater (o, s) {
  const a = num(o.slot.startTime)
  const b = num(s.slot.startTime)
  return a > b || (a === b && o.key > s.key)
}

// Same person for the one-running rule.  An unstamped (pre-1.0.13) v1 slot is
// shown to whoever is signed in (visibleSlotsForUser), so it counts as theirs.
function belongsTo (entry, uid) {
  const su = entry.slot.uid
  if (su == null || su === '') return !isV2(entry.slot)
  return String(su) === String(uid)
}

function cutSource (o, s) {
  return o.key !== s.key && !o.malformed && o.slot && !o.hidden && isTicking(o.slot) &&
    isLater(o, s) && belongsTo(o, s.slot.uid)
}

// min(now, the earliest startTime later than S's among the same person's
// ticking slots, v1 or v2) — the instant S stopped counting.
function cutFor (s, entries, now) {
  let cut = now
  for (const o of entries) {
    if (!o || !cutSource(o, s)) continue
    const st = num(o.slot.startTime)
    if (st < cut) cut = st
  }
  return Math.min(cut, now)
}

// Effective elapsed seconds: a ticking v2 slot stops counting where a later
// timer of the same person started (the read-time implicit pause), so totals
// and saves never double-count even before anything is written.  v1 slots are
// never truncated — an old window really is counting them.
export function effectiveElapsed (entry, entries, now) {
  if (!entry || !entry.slot) return 0
  const slot = entry.slot
  if (!isTicking(slot)) return baseSeconds(slot)
  if (!isV2(slot)) return rawElapsed(slot, now)
  return rawElapsed(slot, cutFor(entry, Array.isArray(entries) ? entries : [], now))
}

// The reconciler's plan (amendment 1): the winner is my ticking slot (v1 or
// v2) with the greatest startTime (ties by key); every OTHER ticking v2 slot
// of mine is paused at its own cut — min(now, the earliest later startTime) —
// the same cut effectiveElapsed uses, so the persisted totals add up to wall
// time.  v1 slots are never written; slots with a fresh claim are left alone.
export function planSingleRunning (entries, uid, now) {
  const list = (Array.isArray(entries) ? entries : []).filter(e =>
    e && e.slot && !e.malformed && !e.hidden && isTicking(e.slot) && belongsTo(e, uid))
  const plan = []
  for (const e of list) {
    if (!isV2(e.slot)) continue
    if (claimIsFresh(e.slot.claim, now)) continue
    if (list.some(o => cutSource(o, e))) plan.push({ key: e.key, at: cutFor(e, list, now) })
  }
  return plan
}

// ── transitions (pure) ───────────────────────────────────────────────────
function withSig (slot) {
  return { ...slot, sig: sigOf(slot) }
}

function pausedClock (slot, at) {
  const next = { ...slot, paused: true, pausedSeconds: rawElapsed(slot, at), pausedAt: at }
  delete next.hours_typed
  return withSig(next)
}

function resumedClock (slot, at) {
  const next = { ...slot, paused: false, startTime: at, pausedSeconds: baseSeconds(slot), ran: true }
  delete next.pausedAt
  delete next.hours_typed
  return withSig(next)
}

// Adoption keeps the key and every field, adding the v2 markers.  It never
// moves the clock: whether an adopted TICKING slot keeps ticking or is paused
// at the adoption instant is decided by the caller (adoptV1 / pause /
// runOneRunning), never by re-basing startTime.  A slot with no `date` (the
// legacy in-page timer never wrote one) is stamped with the firm-zone day of
// its own start, so a later resume — which moves startTime — cannot move the
// entry to another day ("the entry is dated the day the timer started").
function adoptedSlot (slot, uid, tz) {
  const next = { ...slot, v: 2, ran: true, billing_touched: true }
  if (!dateOnly(slot.date)) {
    const d = firmDateIso(num(slot.created_at, 0) || num(slot.startTime, Date.now()), tz)
    if (d) next.date = d
  }
  if (!(typeof slot.tid === 'string' && UUID_RE.test(slot.tid))) next.tid = newUuid()
  if (slot.uid == null || slot.uid === '') {
    next.uid = String(uid)
    next.uid_assumed = true
  }
  return withSig(next)
}

// Hours a save of this slot bills: a paused slot with typed hours bills those
// (rounded up to the 0.1 grid like everything else), otherwise the seconds
// rounded up — billableHoursFromSeconds, max(0.1, ceil(sec/360)/10) on any
// non-zero work.
export function slotSaveHours (slot, seconds) {
  const ht = Number(slot && slot.hours_typed)
  if (slot && slot.paused && Number.isFinite(ht) && ht > 0) return billableHoursFromSeconds(Math.round(ht * 3600))
  return billableHoursFromSeconds(seconds != null ? seconds : baseSeconds(slot))
}

function slotDate (slot) {
  const d = dateOnly(slot && slot.date)
  if (d) return d
  const at = num(slot && slot.created_at, 0) || num(slot && slot.startTime, Date.now())
  return firmDateIso(at, firmTz())
}

// The entry's start time as this slot means it — 'HH:MM', or '' for none:
//   • a string start_time: one the person typed (start_time_touched), or the
//     stored one a resume slot was seeded with (seedResume);
//   • otherwise a resume slot from before start_time existed → '' (unknown —
//     and its PUT leaves the stored value alone);
//   • otherwise a new timer → the firm-local time of day it FIRST started:
//     created_at, which a pause / resume never moves (startTime does);
//   • an old-code slot with no created_at → ''.
export function slotStartTime (slot, tz = firmTz()) {
  const s = slot || {}
  if (typeof s.start_time === 'string') return normalizeStartTime(s.start_time) || ''
  if (s.resume_entry_id != null && s.resume_entry_id !== '') return ''
  const at = num(s.created_at, 0)
  return at > 0 ? firmTimeOfDay(at, tz) : ''
}

// The POST body — a WHITELIST.  Never uid / v / sig / claim / any marker;
// user_id only from slot.user_id (on behalf of someone else); client_ref = tid
// so the server can recognise a retried save (time_entries.client_ref).
export function buildPostBody (slot, { hours, postImmediately = false } = {}) {
  const s = slot || {}
  const h = hours != null ? hours : slotSaveHours(s)
  const body = {
    matter_id: cleanMatterId(s.matter_id) || '',
    date: slotDate(s),
    hours: String(h),
    description: str(s.description),
    activity_code: str(s.activity_code),
    matter_phase_id: cleanPhase(s.matter_phase_id) ?? null,
    post_immediately: postImmediately === true,
    start_time: slotStartTime(s) || null
  }
  const rate = cleanRate(s.rate)
  if (rate) body.rate = rate
  if (BILLING_STATUSES.includes(s.billing_status)) body.billing_status = s.billing_status
  const onBehalf = cleanUserId(s.user_id)
  if (onBehalf && String(onBehalf) !== String(s.uid)) body.user_id = onBehalf
  if (typeof s.tid === 'string' && UUID_RE.test(s.tid)) body.client_ref = s.tid
  return body
}

// The resume PUT body (saveResumedEntry adds hours, drops status/billed).
// PUT /:id never writes matter_id or user_id, so neither is sent.  start_time
// only when the person set it (start_time_touched — markReplayed sets it for
// a replayed new entry): omitted, the PUT keeps the entry's stored one.
export function buildPutBody (slot) {
  const s = slot || {}
  const body = {
    date: slotDate(s),
    description: str(s.description),
    activity_code: str(s.activity_code)
  }
  const phase = cleanPhase(s.matter_phase_id)
  if (phase !== undefined) body.matter_phase_id = phase
  const rate = cleanRate(s.rate)
  if (rate) body.rate = rate
  if (BILLING_STATUSES.includes(s.billing_status)) body.billing_status = s.billing_status
  if (s.start_time_touched === true) body.start_time = normalizeStartTime(s.start_time) || null
  return body
}

// Amendment 7: on replayed:true compare every whitelisted field with the slot.
export function replayDiff (slot, row, { hours } = {}) {
  const s = slot || {}
  const r = row || {}
  const fields = []
  const h = hours != null ? Number(hours) : slotSaveHours(s)
  if (!(Math.abs(num(r.hours, NaN) - h) < 0.001)) fields.push('hours')
  if (str(r.description) !== str(s.description)) fields.push('description')
  if (s.rate_touched && cleanRate(s.rate) && !(Math.abs(num(r.rate, NaN) - Number(s.rate)) < 0.005)) fields.push('rate')
  if (BILLING_STATUSES.includes(s.billing_status) && r.billing_status !== s.billing_status) fields.push('billing_status')
  if (str(r.activity_code) !== str(s.activity_code)) fields.push('activity_code')
  if ((cleanPhase(r.matter_phase_id) ?? null) !== (cleanPhase(s.matter_phase_id) ?? null)) fields.push('matter_phase_id')
  if (dateOnly(str(r.date)) !== slotDate(s)) fields.push('date')
  // Only a row that carries the column (a server from before start_time
  // answers without it — nothing to compare).
  if ('start_time' in r && (normalizeStartTime(r.start_time) || '') !== slotStartTime(s)) fields.push('start_time')
  return { differs: fields.length > 0, fields }
}

// Amendment 8, as a tri-state that maps straight onto releaseClaim's
// `uncertain` option:
//   true      — no definitive answer (no response, network error, timeout,
//               5xx): the POST may have landed → stamp post_uncertain.
//   false     — a definitive answer from the route itself, which runs its
//               client_ref lookup right after authentication: 2xx, 400, 404,
//               409, 422 → clear post_uncertain.
//   undefined — refused before the client_ref lookup (401, 403, 408, 429 and
//               any other 4xx): says nothing about an EARLIER attempt, so any
//               post_uncertain stays exactly as it is.
const DEFINITIVE_STATUSES = new Set([400, 404, 409, 422])
export function postUncertainty (err) {
  if (!err) return true
  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' || err.code === 'ERR_NETWORK') return true
  const st = err.response && Number(err.response.status)
  if (!err.response || !Number.isFinite(st) || st === 0) return true
  if (st >= 500) return true
  if (st < 300 || DEFINITIVE_STATUSES.has(st)) return false
  return undefined
}

// The boolean form, conservative: anything but a definitive answer counts as
// uncertain (so a retry refused by auth or rate limiting never unlocks the
// matter).  Prefer releaseClaim(key, claimId, { error }) — it applies the
// tri-state and leaves an earlier post_uncertain alone on a 401/403/429.
export function isUncertainPostError (err) {
  return postUncertainty(err) !== false
}

// The line to show for a 409 on POST /api/time-entries (response.data).
//   code 'client_ref_deleted' (no existing_entry_id): saved, then deleted
//   during a concurrent save — a later Save records it again;
//   otherwise 'Already saved as entry #N on another matter', or 'Already
//   saved as another time entry' when the 409 carries no id (the server sends
//   it only to a caller who could open that entry).
export function saveConflictMessage (data) {
  const d = data && typeof data === 'object' ? data : {}
  if (d.code === 'client_ref_deleted') return MESSAGES.clientRefDeleted
  return MESSAGES.alreadySavedOther(d.existing_entry_id)
}

// A bar command's cmd.at as the transition time: never before the slot's own
// last pause/start, never after now (amendment 2 — every pause and cut ≤ now).
export function clampCommandAt (slot, at, now, action = 'pause') {
  const a = num(at, now)
  const lower = action === 'resume'
    ? (num(slot && slot.pausedAt, 0) || num(slot && slot.startTime, 0))
    : num(slot && slot.startTime, 0)
  return Math.min(now, Math.max(lower, a))
}

// "<label>" for messages — a matter name or the description; never empty.
const labelOf = (slot) => slotLabel(slot)

// ── stored slot parsing ──────────────────────────────────────────────────
// { slot } | { malformed: true } | null.  null = absent or not a timer (a
// record without running:true is how old code wrote "stopped").  Malformed
// (amendment 2): bad JSON, a non-object, a NaN/zero startTime or a
// negative / non-numeric pausedSeconds — skipped, never thrown on, never
// written, and never allowed to hide a valid timer.
export function parseStoredSlot (raw) {
  if (raw == null || raw === '') return null
  if (typeof raw !== 'string') return { malformed: true }
  let t
  try { t = JSON.parse(raw) } catch { return { malformed: true } }
  if (!t || typeof t !== 'object' || Array.isArray(t)) return { malformed: true }
  if (t.running !== true) return null
  const st = Number(t.startTime)
  if (!Number.isFinite(st) || st <= 0) return { malformed: true }
  if (t.pausedSeconds != null) {
    const ps = Number(t.pausedSeconds)
    if (!Number.isFinite(ps) || ps < 0) return { malformed: true }
  }
  return { slot: t }
}

// ── token uid + broadcast (window-bound defaults) ────────────────────────
export function tokenUid () {
  try { return decodeTokenUserId(getToken()) } catch { return null }
}

export function broadcastTimerEvent (type, payload = {}) {
  try {
    window.localStorage.setItem(BROADCAST_KEY, JSON.stringify({ type, ...payload, at: Date.now() }))
  } catch { /* storage unavailable — same-window listeners still hear the event */ }
  try { window.dispatchEvent(new Event('timerEntrySaved')) } catch { /* no window */ }
}

const windowStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}

// The firm's default billing status as Layout last loaded it (/api/settings),
// for a window that never loads settings — the desktop floating bar, whose
// one-click start would otherwise always be 'billable'.  null when absent or
// not a billing status.
export function readFirmDefaultBillingStatus (storage) {
  try {
    const s = storage || windowStorage()
    const v = s ? s.getItem(FIRM_DEFAULT_BILLING_KEY) : null
    return BILLING_STATUSES.includes(v) ? v : null
  } catch {
    return null
  }
}

// Layout calls this with the value /api/settings returned.  Only a billing
// status is kept; nothing is written when it is already there.
export function rememberFirmDefaultBillingStatus (value, storage) {
  if (!BILLING_STATUSES.includes(value)) return false
  try {
    const s = storage || windowStorage()
    if (!s) return false
    if (s.getItem(FIRM_DEFAULT_BILLING_KEY) !== value) s.setItem(FIRM_DEFAULT_BILLING_KEY, value)
    return true
  } catch {
    return false
  }
}

function defaultIsDesktopMain () {
  try {
    const e = window.electronAPI
    return !!(e && e.isElectron) && !String(window.location.pathname || '').startsWith('/popup/')
  } catch {
    return false
  }
}

function electronProbe () {
  try {
    const e = window.electronAPI
    if (e && typeof e.isPopupOpen === 'function') return (pk) => window.electronAPI.isPopupOpen(pk)
  } catch { /* no window */ }
  return null
}

// Fields that are NOT field merges (the clobber journal handles them as clock
// state or never touches them).
const CLOCK_KEYS = ['paused', 'startTime', 'pausedSeconds', 'pausedAt', 'hours_typed', 'claim']
const NON_FIELD_KEYS = new Set([...CLOCK_KEYS, 'running', 'v', 'sig', 'uid', 'uid_assumed', 'tid', 'ran', 'created_at'])
const SETTABLE_FIELDS = new Set(['description', 'activity_code', 'billing_status', 'billing_touched', 'rate', 'rate_touched', 'matter_phase_id', 'date', 'start_time', 'start_time_touched'])

const clockOf = (s) => (s ? {
  paused: !!s.paused,
  startTime: s.startTime ?? null,
  pausedSeconds: s.pausedSeconds ?? null,
  pausedAt: s.pausedAt ?? null,
  hours_typed: s.hours_typed ?? null,
  claim: s.claim ?? null
} : null)

function applyClock (slot, c) {
  const next = { ...slot, paused: !!c.paused, startTime: c.startTime, pausedSeconds: c.pausedSeconds }
  for (const k of ['pausedAt', 'hours_typed', 'claim']) {
    if (c[k] == null) delete next[k]
    else next[k] = c[k]
  }
  return withSig(next)
}

const pushPrior = (list, value, after) => {
  const out = list.filter(p => !same(p, after))
  if (!same(value, after) && !out.some(p => same(p, value))) out.push(value)
  return out
}

// ══════════════════════════════════════════════════════════════════════════
export function createTimerStore ({
  storage = null,
  now = () => Date.now(),
  dispatch = null,
  subscribe = null,
  broadcast = null,
  getUid = null,
  env = {}
} = {}) {
  const S = () => {
    try { return typeof storage === 'function' ? storage() : storage } catch { return null }
  }
  // This window's copy of the firm default (Layout, or a pop-out that loaded
  // /api/settings), else the copy Layout keeps in storage (the floating bar
  // never loads settings), else today's fallback ('billable').
  const defaultFirmBilling = () => {
    let w = null
    try { w = typeof window !== 'undefined' ? window.__lextrackDefaultBillingStatus : null } catch { w = null }
    if (BILLING_STATUSES.includes(w)) return w
    const s = S()
    return (s ? readFirmDefaultBillingStatus(s) : null) || firmDefaultBillingStatus()
  }
  const E = {
    hasTimerBar: () => hasTimerBar(),
    isDesktopMain: defaultIsDesktopMain,
    isPopupOpen: undefined,
    firmTz: () => firmTz(),
    firmDefaultBillingStatus: defaultFirmBilling,
    sendCommand: (key, action, opts) => sendTimerCommand(key, action, opts),
    tabId: () => getTabId(),
    ...env
  }
  const probeFn = () => (E.isPopupOpen === undefined ? electronProbe() : (typeof E.isPopupOpen === 'function' ? E.isPopupOpen : null))
  const clock = () => {
    const n = Number(now())
    return Number.isFinite(n) ? n : Date.now()
  }
  const resolveUid = (uid) => {
    if (uid === undefined) {
      try { const u = typeof getUid === 'function' ? getUid() : tokenUid(); return u == null || u === '' ? null : String(u) } catch { return null }
    }
    return uid == null || uid === '' ? null : String(uid)
  }
  const emit = (detail) => {
    if (typeof dispatch !== 'function') return
    try { dispatch(detail) } catch { /* a listener must never break a write */ }
  }
  const sendBroadcast = (type, payload) => {
    try {
      if (typeof broadcast === 'function') broadcast(type, payload)
    } catch { /* best effort */ }
  }

  // ── reads ──────────────────────────────────────────────────────────────
  function entryFrom (s, key, t) {
    let raw
    try { raw = s.getItem(key) } catch { return { key, error: true } }
    const parsed = parseStoredSlot(raw)
    if (!parsed) return null
    if (parsed.malformed) return { key, malformed: true }
    const slot = parsed.slot
    let owner = null
    try { owner = parseOwner(s.getItem(ownerKeyFor(key))) } catch { owner = null }
    const st = ownerState(owner, t)
    const v2 = isV2(slot)
    const held = st === 'live' || st === 'quiet'
    const hidden = v2
      ? (slot.ran === false && !slot.dirty && !held)
      : isAbandonedSlot(slot, owner, t, key)
    return { key, slot, owner, ownerState: st, v2, held, hidden }
  }

  function readEntry (key, t) {
    const s = S()
    if (!s) return { key, error: true }
    return entryFrom(s, key, t)
  }

  function readAll (t) {
    const s = S()
    const out = []
    if (!s) return out
    let n = 0
    try { n = s.length } catch { return out }
    const keys = []
    for (let i = 0; i < n; i++) {
      let k = null
      try { k = s.key(i) } catch { k = null }
      if (isTimerSlotKey(k)) keys.push(k)
    }
    for (const k of keys) {
      const e = entryFrom(s, k, t)
      if (e && !e.error) out.push(e)
    }
    return out
  }

  function classifyEntry (e) {
    if (!e || !e.slot) return null
    // An old-bundle window (a fresh owner record without v) holds it — a
    // desktop 1.0.13/14 pop-out still on the pre-update page.  Its clock is in
    // React refs: reach it only by command, whatever the slot says.
    if (e.held && e.owner && e.owner.v !== 2) return { cls: 'v1-held', probe: null }
    if (e.v2) return { cls: 'v2', probe: null }
    if (isLegacySlotKey(e.key)) {
      // The desktop main window is single-instance and pop-outs never hold
      // the legacy key, so here nothing else can tick it.  In a browser other
      // old tabs may.
      return { cls: E.isDesktopMain() ? 'adopt-silently' : 'v1-unknown', probe: null }
    }
    if (isResumeSlotKey(e.key)) {
      const probe = probeFn() ? `timer-resume-${resumeEntryIdOfKey(e.key)}` : null
      return { cls: 'v1-unknown', probe }
    }
    // Old pop-outs on desktop 1.0.13+ always heartbeat, so unowned = closed.
    return { cls: E.hasTimerBar() ? 'adopt-silently' : 'v1-unknown', probe: null }
  }

  const mine = (e, uid) => !!(e && e.slot) && belongsTo(e, uid)

  function rowFor (e, entries, t, uid) {
    const c = classifyEntry(e) || { cls: 'v1-unknown', probe: null }
    const slot = e.slot
    const elapsed = effectiveElapsed(e, entries, t)
    const raw = isTicking(slot)
    const truncated = raw && e.v2 && cutFor(e, entries, t) < t
    const tab = E.tabId()
    const matterId = cleanMatterId(slot.matter_id) || ''
    const resumeId = slot.resume_entry_id != null && slot.resume_entry_id !== ''
      ? String(slot.resume_entry_id)
      : resumeEntryIdOfKey(e.key)
    return {
      key: e.key,
      slot,
      cls: c.cls,
      probe: c.probe,
      v2: e.v2,
      ticking: raw && !truncated,
      implicitlyPaused: !!truncated,
      elapsed,
      label: labelOf(slot),
      matterId,
      matterTitle: matterId ? str(slot.matter_title) : '',
      hasMatter: !!matterId,
      description: str(slot.description),
      isResume: !!resumeId,
      resumeEntryId: resumeId || null,
      onBehalfOf: cleanUserId(slot.user_id) && String(cleanUserId(slot.user_id)) !== String(slot.uid) ? String(cleanUserId(slot.user_id)) : null,
      claimed: claimIsFresh(slot.claim, t),
      postUncertain: !!slot.post_uncertain,
      uidAssumed: !!slot.uid_assumed,
      owner: e.owner,
      ownerState: e.ownerState,
      heldHere: !!(e.held && e.owner && e.owner.tab && e.owner.tab === tab),
      heldElsewhere: !!(e.held && !(e.owner && e.owner.tab && e.owner.tab === tab)),
      futureStart: num(slot.startTime) - t > FUTURE_START_MS,
      longRunning: elapsed > LONG_RUNNING_SECONDS,
      fromBeforeUpdate: !e.v2 || c.cls === 'v1-held',
      hidden: !!e.hidden,
      mine: uid != null && mine(e, uid),
      lastActivity: lastActivity(slot)
    }
  }

  const byRow = (a, b) => {
    if (a.ticking !== b.ticking) return a.ticking ? -1 : 1
    const d = b.lastActivity - a.lastActivity
    if (d !== 0) return d
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  }

  function listMyTimers (uid, { now: at } = {}) {
    const t = at != null ? num(at, clock()) : clock()
    const u = resolveUid(uid)
    if (u == null) return []
    const entries = readAll(t)
    const rows = []
    for (const e of entries) {
      if (e.malformed || !e.slot || e.hidden) continue
      if (e.v2 ? String(e.slot.uid) !== u : !mine(e, u)) continue
      rows.push(rowFor(e, entries, t, u))
    }
    return rows.sort(byRow)
  }

  function getTimer (key, { uid, now: at } = {}) {
    if (!isTimerSlotKey(key)) return null
    const t = at != null ? num(at, clock()) : clock()
    const entries = readAll(t)
    const e = entries.find(x => x.key === key)
    if (!e || e.malformed || !e.slot) return null
    return rowFor(e, entries, t, resolveUid(uid))
  }

  function readSlot (key) {
    if (!isTimerSlotKey(key)) return null
    const e = readEntry(key, clock())
    return e && e.slot ? e.slot : null
  }

  function classify (key, { now: at } = {}) {
    if (!isTimerSlotKey(key)) return null
    return classifyEntry(readEntry(key, at != null ? num(at, clock()) : clock()))
  }

  function effectiveElapsedSeconds (key, { now: at } = {}) {
    const t = at != null ? num(at, clock()) : clock()
    const entries = readAll(t)
    const e = entries.find(x => x.key === key)
    return e && e.slot ? effectiveElapsed(e, entries, t) : 0
  }

  // ── the clobber journal (amendment 3) ─────────────────────────────────
  // Per key, this window's last clock transition and its field merges from
  // the last JOURNAL_MS, plus the tid of a slot it removed.  A storage event
  // that puts back a value this window had already replaced — another
  // renderer wrote from a stale read — gets this window's write re-applied
  // once (same T, so idempotent), on a fresh read and only where the fresh
  // value still shows the revert.  One re-apply per entry bounds any
  // ping-pong between two windows.
  const journal = new Map()
  // Claim ids this window issued (bounded): a re-applied clock keeps an
  // incoming fresh claim only when it is ANOTHER window's in-flight save.
  const ownClaims = new Set()
  let unsubscribe = null
  const ensureSubscribed = () => {
    if (unsubscribe || typeof subscribe !== 'function') return
    try { unsubscribe = subscribe((e) => handleStorageEvent(e)) || (() => {}) } catch { unsubscribe = () => {} }
  }

  function journalWrite (key, prev, next, kind, t) {
    let j = journal.get(key)
    if (!j) { j = { fields: {} }; journal.set(key, j) }
    // One timer instance per journal: a different tid at this key (a resume
    // key discarded and seeded again) starts it afresh.
    const tid = next && typeof next.tid === 'string' ? next.tid : null
    if (j.tid !== tid) { j.clock = null; j.fields = {}; j.tid = tid }
    if (!prev) { delete j.removed; return }
    const pc = clockOf(prev)
    const nc = clockOf(next)
    if (!same(pc, nc)) {
      const old = j.clock && t - j.clock.at < JOURNAL_MS ? j.clock : null
      let priors = old ? pushPrior(old.priors, old.after, nc) : []
      priors = pushPrior(priors, pc, nc)
      j.clock = { at: t, kind, after: nc, priors, used: false }
    }
    const keys = new Set([...Object.keys(prev), ...Object.keys(next)])
    for (const k of keys) {
      if (NON_FIELD_KEYS.has(k) || same(prev[k], next[k])) continue
      const old = j.fields[k] && t - j.fields[k].at < JOURNAL_MS ? j.fields[k] : null
      let priors = old ? pushPrior(old.priors, old.after, next[k]) : []
      priors = pushPrior(priors, prev[k], next[k])
      j.fields[k] = { at: t, after: next[k], priors, used: false }
    }
  }

  function handleStorageEvent (ev) {
    if (!ev || typeof ev.key !== 'string' || !isTimerSlotKey(ev.key)) return
    const s = S()
    if (!s) return
    if (ev.storageArea && ev.storageArea !== s) return
    const j = journal.get(ev.key)
    if (!j) return
    const t = clock()
    const parsed = parseStoredSlot(ev.newValue)
    const incoming = parsed && parsed.slot ? parsed.slot : null
    // A slot this window saved or discarded, written back by a stale reader.
    if (incoming && j.removed && !j.removed.used && t - j.removed.at < JOURNAL_MS &&
        j.removed.tid && incoming.tid === j.removed.tid) {
      j.removed.used = true
      try {
        s.removeItem(ev.key)
        s.removeItem(ownerKeyFor(ev.key))
      } catch { return }
      emit({ key: ev.key, action: 'reapply' })
      return
    }
    if (!incoming) return
    // Another timer instance now lives at this key: our journal is not about it.
    if (incoming.tid !== j.tid) {
      j.clock = null
      j.fields = {}
      return
    }
    let fixClock = null
    const jc = j.clock
    if (jc && !jc.used && t - jc.at < JOURNAL_MS) {
      const c = clockOf(incoming)
      if (!same(c, jc.after)) {
        const reverted = jc.priors.some(p => same(p, c))
        // Two pauses of the same run: the earlier one is the truth (the clock
        // stopped there); a later one came from a read that missed ours.
        const laterPause = !!jc.after.paused && !!c.paused && c.startTime === jc.after.startTime &&
          num(c.pausedAt, -Infinity) > num(jc.after.pausedAt, Infinity)
        if (reverted || laterPause) {
          fixClock = jc.after
          // Another window's save is in flight on this slot (its fresh claim
          // came from a read that missed our pause): keep that claim, so no
          // second claim — and no second POST — can start meanwhile.
          if (!jc.after.claim && c.claim && claimIsFresh(c.claim, t) && !ownClaims.has(c.claim.id)) {
            fixClock = { ...jc.after, claim: c.claim }
          }
        }
      }
    }
    const fixFields = {}
    for (const [k, f] of Object.entries(j.fields)) {
      if (f.used || t - f.at >= JOURNAL_MS) continue
      if (!same(incoming[k], f.after) && f.priors.some(p => same(p, incoming[k]))) fixFields[k] = f
    }
    if (!fixClock && Object.keys(fixFields).length === 0) return
    const fresh = readEntry(ev.key, t)
    if (!fresh || !fresh.slot || !fresh.v2 || fresh.slot.tid !== incoming.tid) return
    let next = { ...fresh.slot }
    let changed = false
    if (fixClock && same(clockOf(fresh.slot), clockOf(incoming))) {
      next = applyClock(next, fixClock)
      jc.used = true
      changed = true
    }
    for (const [k, f] of Object.entries(fixFields)) {
      if (!same(fresh.slot[k], incoming[k])) continue
      if (f.after === undefined) delete next[k]
      else next[k] = f.after
      f.used = true
      changed = true
    }
    if (!changed) return
    try { s.setItem(ev.key, JSON.stringify(withSig(next))) } catch { return }
    emit({ key: ev.key, action: 'reapply' })
  }

  // ── writes ─────────────────────────────────────────────────────────────
  // Write `next` over `prev` (the slot this transition was computed from).
  // Returns 'unchanged' | true | false (storage exception).
  function commit (key, prev, next, kind, t) {
    const s = S()
    if (!s) return false
    const out = JSON.stringify(next)
    let cur = null
    try { cur = s.getItem(key) } catch { return false }
    if (cur === out) return 'unchanged'
    try { s.setItem(key, out) } catch { return false }
    journalWrite(key, prev, next, kind, t)
    ensureSubscribed()
    emit({ key, action: kind })
    return true
  }

  function removeSlot (key, prev, kind, t) {
    const s = S()
    if (!s) return false
    try {
      s.removeItem(key)
      s.removeItem(ownerKeyFor(key))
    } catch { return false }
    const j = journal.get(key) || { fields: {} }
    j.clock = null
    j.fields = {}
    j.removed = prev && prev.tid ? { at: t, tid: prev.tid, used: false } : null
    journal.set(key, j)
    ensureSubscribed()
    emit({ key, action: kind })
    return true
  }

  // ── the removal note ─────────────────────────────────────────────────
  // 'lextrack_timer_removed:<uid>' = [{ key, tid, why, at }], pruned to
  // REMOVED_NOTE_MS on every write.  Written BEFORE the slot goes, so this
  // window's TIMER_SLOT_EVENT and other windows' storage events for the
  // removal both find it.  Per uid; holds no matter name and no fields.
  const removedKey = (u) => `${REMOVED_PREFIX}${u}`

  function readNotes (s, u, t) {
    let list = []
    try {
      const v = JSON.parse(s.getItem(removedKey(u)) || '[]')
      if (Array.isArray(v)) list = v
    } catch { list = [] }
    return list.filter(n => n && typeof n === 'object' && typeof n.key === 'string' &&
      Number.isFinite(Number(n.at)) && Math.abs(t - Number(n.at)) < REMOVED_NOTE_MS)
  }

  function writeNote (u, key, tid, why, t) {
    const s = S()
    if (!s) return false
    const list = readNotes(s, u, t).filter(n => n.key !== key)
    list.push({ key, tid: typeof tid === 'string' ? tid : null, why, at: t })
    try { s.setItem(removedKey(u), JSON.stringify(list.slice(-20))) } catch { return false }
    return true
  }

  function dropNote (u, key, t) {
    const s = S()
    if (!s) return
    const left = readNotes(s, u, t).filter(n => n.key !== key)
    try {
      if (left.length) s.setItem(removedKey(u), JSON.stringify(left))
      else s.removeItem(removedKey(u))
    } catch { /* best effort — an unmatched note only changes a message */ }
  }

  function removalNote (key, { uid, tid, now: at } = {}) {
    if (!isTimerSlotKey(key)) return null
    const u = resolveUid(uid)
    if (u == null) return null
    const s = S()
    if (!s) return null
    const t = at != null ? num(at, clock()) : clock()
    const hit = readNotes(s, u, t).reverse().find(n => n.key === key &&
      (tid == null || tid === '' || n.tid == null || n.tid === tid))
    if (hit && hit.why === 'blocked') return { why: 'blocked', message: MESSAGES.blockedRemoved }
    return null
  }

  const written = (key, w, slot, extra = {}) => {
    if (w === false) return fail('storage', { key })
    return { ok: true, key, changed: w === true, slot, ...extra }
  }

  // Load a slot for a write by `uid`: valid key, readable, present, parseable,
  // and not someone else's (an unstamped v1 slot passes — adoption stamps it).
  function loadForWrite (key, uid, t) {
    if (!isTimerSlotKey(key)) return fail('invalid', { key })
    if (uid == null) return fail('no-user', { key })
    const e = readEntry(key, t)
    if (!e) return fail('missing', { key })
    if (e.error) return fail('storage', { key })
    if (e.malformed) return fail('malformed', { key })
    if (e.slot.uid != null && e.slot.uid !== '' && String(e.slot.uid) !== uid) return fail('foreign', { key })
    return { ok: true, entry: e }
  }

  // Load a v2 slot of mine for a FIELD merge.
  function loadV2 (key, uid, t) {
    const L = loadForWrite(key, uid, t)
    if (!L.ok) return L
    const c = classifyEntry(L.entry)
    if (c.cls === 'v1-held') return fail('held', { key })
    if (c.cls !== 'v2') return fail('needs-adopt', { key, cls: c.cls, probe: c.probe })
    if (claimIsFresh(L.entry.slot.claim, t)) return fail('claimed', { key })
    return L
  }

  // Write the reconciler's plan (amendment 1) from `entries`, synchronously.
  // Called before any transition that STOPS or REMOVES a ticking slot: while
  // a slot ticks it cuts my earlier ticking v2 timers at its start (the
  // read-time implicit pause); once it stops, that cut would vanish, the
  // earlier timer would count those seconds a second time and would read as
  // ticking again — a resume nobody asked for.  `entries` must be the
  // snapshot the caller computed its own cut from; `except` (a key or a Set)
  // is written by the caller itself.  `T` bounds the cut (default now).
  function settleFrom (entries, uid, t, except = null, T = t) {
    const skip = except instanceof Set ? except : new Set(except ? [except] : [])
    const wrote = []
    let ok = true
    for (const p of planSingleRunning(entries, uid, T)) {
      if (skip.has(p.key)) continue
      const e = entries.find(x => x.key === p.key)
      const fresh = readEntry(p.key, t)
      if (!e || !fresh || !fresh.slot || !fresh.v2 || !isTicking(fresh.slot)) continue
      if (fresh.slot.startTime !== e.slot.startTime || claimIsFresh(fresh.slot.claim, t)) continue
      const w = commit(p.key, fresh.slot, pausedClock(fresh.slot, p.at), 'reconcile', t)
      if (w === false) ok = false
      else if (w === true) wrote.push(p.key)
    }
    return { ok, wrote }
  }

  const heldInfo = (e, cmd) => ({
    key: e.key,
    cmd,
    pk: (e.owner && e.owner.pk) || null,
    label: labelOf(e.slot)
  })

  // Step 2 of START / RESUME: find my other ticking timers an OLD tab or
  // window may be counting.  Resume slots whose window isPopupOpen says is
  // closed are safe to adopt silently.
  function scanUnknown (targetKey, uid, t) {
    const entries = readAll(t)
    const unknown = []
    const probes = []
    for (const e of entries) {
      if (e.key === targetKey || e.malformed || !e.slot || e.hidden || !mine(e, uid) || !isTicking(e.slot)) continue
      const c = classifyEntry(e)
      if (c.cls !== 'v1-unknown') continue
      if (c.probe) probes.push({ e, probe: c.probe })
      else unknown.push(e)
    }
    return { unknown, probes }
  }

  async function settleUnknown (scan, opts) {
    const silent = new Set()
    const unknown = scan.unknown.slice()
    const probe = probeFn()
    for (const p of scan.probes) {
      let open = true
      try { open = probe ? !!(await probe(p.probe)) : true } catch { open = true }
      if (open) unknown.push(p.e)
      else silent.add(p.e.key)
    }
    if (unknown.length) {
      const label = labelOf(unknown[0].slot)
      const keys = unknown.map(x => x.key)
      if (typeof opts.confirm !== 'function') return fail('needs-confirm', { kind: 'start-anyway', keys, label })
      let yes = false
      try {
        yes = await opts.confirm({
          kind: 'start-anyway', keys, label, danger: true,
          confirmLabel: 'Start anyway', message: MESSAGES.startAnyway(label)
        })
      } catch { yes = false }
      if (yes !== true) return fail('cancelled', { keys })
    }
    return { ok: true, silent }
  }

  // Step 3: synchronously, at one T.  `target` = { key, next(prevSlotOrNull,
  // T) → slot } or { key, viaCommand: 'resume' }.  The target is written FIRST
  // so a storage failure leaves everything as it was; then every other
  // ticking v2 timer of mine (no fresh claim) is paused at min(T, its cut),
  // adopt-silently ones are adopted + paused at T, and v1-held ones get
  // 'pause'.
  //
  // T is clamped to [the last instant any OTHER timer of mine has counted —
  // a ticking one's startTime, a paused one's pausedAt — , now].  A T in the
  // past would otherwise pause a timer before its own start (its seconds
  // lost) and let the target count time another timer already counted.
  function runOneRunning (target, uid, Tin, t, silent) {
    const entries = readAll(t)
    const before = entries.find(x => x.key === target.key) || null
    let floor = -Infinity
    for (const e of entries) {
      if (e.key === target.key || e.malformed || !e.slot || e.hidden || !mine(e, uid)) continue
      const last = isTicking(e.slot) ? num(e.slot.startTime, -Infinity) : num(e.slot.pausedAt, -Infinity)
      if (last > floor) floor = last
    }
    const T = Math.min(t, Math.max(num(Tin, t), floor))
    let result
    if (target.viaCommand) {
      const cmd = E.sendCommand(target.key, target.viaCommand, { storage: S(), now: T })
      if (!cmd) return fail('storage', { key: target.key })
      result = { ok: true, key: target.key, changed: false, viaCommand: cmd, held: [heldInfo(before, cmd)] }
    } else {
      const next = target.next(before ? before.slot : null, T)
      const w = commit(target.key, before ? before.slot : null, next, target.kind, t)
      if (w === false) return fail('storage', { key: target.key })
      result = { ok: true, key: target.key, changed: w === true, slot: next, held: [] }
    }
    result.at = T
    result.paused = []
    result.adopted = []
    result.failed = []
    for (const e of entries) {
      if (e.key === target.key || e.malformed || !e.slot || e.hidden || !mine(e, uid) || !isTicking(e.slot)) continue
      const c = classifyEntry(e)
      if (c.cls === 'v1-held') {
        const cmd = E.sendCommand(e.key, 'pause', { storage: S(), now: T })
        if (cmd) result.held.push(heldInfo(e, cmd))
        continue
      }
      let slot = e.slot
      let kind = 'pause'
      if (c.cls !== 'v2') {
        if (!(c.cls === 'adopt-silently' || silent.has(e.key))) continue // v1-unknown: confirmed "both will count"
        slot = adoptedSlot(slot, uid, E.firmTz())
        kind = 'adopt'
      }
      if (claimIsFresh(slot.claim, t)) continue
      // v1 time is never truncated (an old window was counting it), so an
      // adopted slot pauses at T with every second it counted; a v2 slot at
      // its own cut, judged from this same snapshot.
      const at = kind === 'adopt' ? T : Math.min(T, cutFor(e, entries, t))
      const w = commit(e.key, e.slot, pausedClock(slot, at), kind, t)
      if (w === false) result.failed.push(e.key)
      else {
        result.paused.push(e.key)
        if (kind === 'adopt') result.adopted.push(e.key)
      }
    }
    return result
  }

  function initialFields (fields, uid, T) {
    const f = fields || {}
    const matterId = cleanMatterId(f.matter_id) || ''
    const status = BILLING_STATUSES.includes(f.billing_status)
      ? f.billing_status
      : deriveDefaultBillingStatus(matterId ? f.billing_type : null, E.firmDefaultBillingStatus())
    const rate = cleanRate(f.rate) || ''
    const onBehalf = cleanUserId(f.user_id) || ''
    return {
      matter_id: matterId,
      matter_title: matterId ? str(f.matter_title) : '',
      description: str(f.description),
      activity_code: str(f.activity_code),
      billing_status: status,
      billing_touched: f.billing_touched === true,
      rate,
      rate_touched: f.rate_touched === true && rate !== '',
      user_id: onBehalf && onBehalf !== uid ? onBehalf : '',
      matter_phase_id: cleanPhase(f.matter_phase_id) ?? null,
      date: dateOnly(f.date) || firmDateIso(T, E.firmTz()),
      // A start time typed before START; otherwise none is stored and the
      // entry starts when the timer first did (slotStartTime: created_at).
      ...(f.start_time_touched === true && normalizeStartTime(f.start_time) != null
        ? { start_time: normalizeStartTime(f.start_time), start_time_touched: true }
        : {})
    }
  }

  async function startNewTimer ({ uid, fields, at, seedSeconds = 0, key, confirm } = {}) {
    const u = resolveUid(uid)
    if (u == null) return fail('no-user')
    const k = key == null ? newSlotKey() : key
    if (!isTimerSlotKey(k) || isLegacySlotKey(k) || isResumeSlotKey(k)) return fail('invalid', { key: k })
    const t0 = clock()
    const existing = readEntry(k, t0)
    if (existing && existing.error) return fail('storage', { key: k })
    if (existing) return fail('exists', { key: k })
    const scan = scanUnknown(k, u, t0)
    let silent = new Set()
    if (scan.unknown.length || scan.probes.length) {
      const settled = await settleUnknown(scan, { confirm })
      if (!settled.ok) return { ...settled, key: k }
      silent = settled.silent
    }
    const t = clock()
    const Tin = at != null ? Math.min(num(at, t), t) : t
    const again = readEntry(k, t)
    if (again) return fail(again.error ? 'storage' : 'exists', { key: k })
    const seed = Math.max(0, Math.floor(num(seedSeconds)))
    return runOneRunning({
      key: k,
      kind: 'start',
      next: (_prev, T) => withSig({
        running: true,
        paused: false,
        startTime: T,
        pausedSeconds: seed,
        ran: true,
        uid: u,
        v: 2,
        tid: newUuid(),
        created_at: T,
        ...initialFields(fields, u, T)
      })
    }, u, Tin, t, silent)
  }

  async function startOrResume (key, { uid, at, confirm } = {}) {
    const u = resolveUid(uid)
    const t0 = clock()
    const L = loadForWrite(key, u, t0)
    if (!L.ok) return L
    let c = classifyEntry(L.entry)
    if (c.cls !== 'v2' && c.cls !== 'v1-held') {
      const a = await adoptV1(key, { uid: u, confirm })
      if (!a.ok) return a
      c = { cls: 'v2' }
    }
    if (c.cls === 'v2' && claimIsFresh(L.entry.slot.claim, t0)) return fail('claimed', { key })
    const scan = scanUnknown(key, u, t0)
    let silent = new Set()
    if (scan.unknown.length || scan.probes.length) {
      const settled = await settleUnknown(scan, { confirm })
      if (!settled.ok) return { ...settled, key }
      silent = settled.silent
    }
    // Synchronous from here: fresh read, one T.
    const t = clock()
    const entries = readAll(t)
    const e = entries.find(x => x.key === key)
    if (!e || !e.slot) return fail('missing', { key })
    const cNow = classifyEntry(e)
    if (cNow.cls === 'v1-held') {
      if (!e.slot.paused) return { ok: true, key, changed: false, slot: e.slot }
      const Tin = at != null ? clampCommandAt(e.slot, at, t, 'resume') : t
      return runOneRunning({ key, viaCommand: 'resume' }, u, Tin, t, silent)
    }
    if (cNow.cls !== 'v2') return fail('needs-adopt', { key, cls: cNow.cls, probe: cNow.probe })
    if (e.slot.uid != null && String(e.slot.uid) !== u) return fail('foreign', { key })
    if (claimIsFresh(e.slot.claim, t)) return fail('claimed', { key })
    const ticking = isTicking(e.slot)
    const cut = ticking ? cutFor(e, entries, t) : t
    if (ticking && cut >= t) return { ok: true, key, changed: false, slot: e.slot, at: t, paused: [], held: [], adopted: [], failed: [] }
    const Tin = at != null ? clampCommandAt(e.slot, at, t, 'resume') : t
    return runOneRunning({
      key,
      kind: 'resume',
      // Implicitly paused (a later timer started while this one still read
      // as ticking): materialise that pause at the cut, then resume at T.
      next: (prev, T) => resumedClock(ticking ? pausedClock(prev, cut) : prev, Math.max(T, ticking ? cut : 0))
    }, u, Tin, t, silent)
  }

  function pause (key, { uid, at } = {}) {
    const u = resolveUid(uid)
    const t = clock()
    const L = loadForWrite(key, u, t)
    if (!L.ok) return L
    const e = L.entry
    const c = classifyEntry(e)
    if (c.cls === 'v1-held') {
      if (!isTicking(e.slot)) return { ok: true, key, changed: false, slot: e.slot }
      const cmd = E.sendCommand(key, 'pause', { storage: S(), now: at != null ? clampCommandAt(e.slot, at, t, 'pause') : t })
      if (!cmd) return fail('storage', { key })
      return { ok: true, key, changed: false, slot: e.slot, viaCommand: cmd, held: [heldInfo(e, cmd)] }
    }
    let slot = e.slot
    let kind = 'pause'
    if (c.cls !== 'v2') {
      if (c.cls !== 'adopt-silently') return fail('needs-adopt', { key, cls: c.cls, probe: c.probe })
      slot = adoptedSlot(slot, u, E.firmTz())
      kind = 'adopt'
    }
    if (claimIsFresh(slot.claim, t)) return fail('claimed', { key })
    if (!isTicking(slot)) {
      if (kind === 'adopt') return written(key, commit(key, e.slot, slot, kind, t), slot)
      return { ok: true, key, changed: false, slot }
    }
    const entries = readAll(t)
    const T = at != null ? clampCommandAt(slot, at, t, 'pause') : t
    // v1 time is never truncated: an adopted slot pauses at T with every
    // second old code counted; a v2 slot at min(T, its cut).
    const stopAt = kind === 'adopt' ? T : Math.min(T, cutFor(e, entries, t))
    settleFrom(entries, u, t, key)
    const next = pausedClock(slot, stopAt)
    return written(key, commit(key, e.slot, next, kind, t), next)
  }

  async function toggle (key, opts = {}) {
    const row = getTimer(key, { uid: opts.uid })
    if (!row) return fail('missing', { key })
    if (row.ticking) {
      const r = pause(key, opts)
      if (r.ok || r.reason !== 'needs-adopt') return r
      const a = await adoptV1(key, { uid: opts.uid, confirm: opts.confirm })
      return a.ok ? pause(key, opts) : a
    }
    return startOrResume(key, opts)
  }

  async function pauseOrStart ({ uid, at, confirm, fields } = {}) {
    const u = resolveUid(uid)
    if (u == null) return fail('no-user')
    const ticking = listMyTimers(u).filter(r => r.ticking)
    if (ticking.length) {
      const results = []
      for (const r of ticking) {
        let res = pause(r.key, { uid: u, at })
        if (!res.ok && res.reason === 'needs-adopt') {
          const a = await adoptV1(r.key, { uid: u, confirm })
          res = a.ok ? pause(r.key, { uid: u, at }) : a
        }
        results.push(res)
      }
      const ok = results.some(r => r.ok)
      return ok
        ? { ok: true, action: 'paused', keys: ticking.map(r => r.key), results, held: results.flatMap(r => r.held || []) }
        : { ...results[0], action: 'paused' }
    }
    const r = await startNewTimer({ uid: u, at, confirm, fields })
    return { ...r, action: 'started' }
  }

  function seedResume (entryId, { uid, entry, seedSeconds, at, entryUpdatedAt, date } = {}) {
    const key = resumeSlotKey(entryId)
    if (!key) return fail('invalid')
    const u = resolveUid(uid)
    if (u == null) return fail('no-user', { key })
    const t = clock()
    const e = readEntry(key, t)
    if (e && e.error) return fail('storage', { key })
    if (e && e.slot) {
      if (e.slot.uid != null && e.slot.uid !== '' && String(e.slot.uid) !== u) return fail('foreign', { key })
      return { ok: true, key, changed: false, existed: true, slot: e.slot }
    }
    const f = entry || {}
    const T = at != null ? Math.min(num(at, t), t) : t
    const rate = cleanRate(f.rate) || ''
    const onBehalf = cleanUserId(f.user_id) || ''
    const slot = withSig({
      running: true,
      paused: true,
      startTime: T,
      pausedSeconds: Math.max(0, Math.floor(num(seedSeconds))),
      pausedAt: T,
      ran: false,
      uid: u,
      v: 2,
      tid: newUuid(),
      created_at: T,
      matter_id: cleanMatterId(f.matter_id) || '',
      matter_title: str(f.matter_title),
      description: str(f.description),
      activity_code: str(f.activity_code),
      billing_status: BILLING_STATUSES.includes(f.billing_status) ? f.billing_status : 'billable',
      billing_touched: true,
      rate,
      rate_touched: rate !== '',
      user_id: onBehalf && onBehalf !== u ? onBehalf : '',
      matter_phase_id: cleanPhase(f.matter_phase_id) ?? null,
      date: dateOnly(date) || dateOnly(str(f.date)) || firmDateIso(T, E.firmTz()),
      // The entry's stored start time, shown in the form; a resumed entry
      // keeps it unless the person edits it (buildPutBody sends it only then).
      start_time: normalizeStartTime(f.start_time) || '',
      start_time_touched: false,
      resume_entry_id: Number(resumeEntryIdOfKey(key)),
      entry_updated_at: entryUpdatedAt != null ? entryUpdatedAt : (f.updated_at != null ? f.updated_at : null)
    })
    return written(key, commit(key, null, slot, 'seed', t), slot, { existed: false })
  }

  // A field merge: never touches clock fields.
  function merge (key, uid, build, kind) {
    const u = resolveUid(uid)
    const t = clock()
    const L = loadV2(key, u, t)
    if (!L.ok) return L
    const prev = L.entry.slot
    const res = build(prev)
    if (!res.ok) return res
    const next = { ...res.next }
    for (const k of CLOCK_KEYS) {
      if (prev[k] === undefined) delete next[k]
      else next[k] = prev[k]
    }
    if (same({ ...next, dirty: prev.dirty }, prev)) return { ok: true, key, changed: false, slot: prev }
    next.dirty = true
    const out = withSig(next)
    return written(key, commit(key, prev, out, kind, t), out)
  }

  function setFields (key, partial, { uid } = {}) {
    return merge(key, uid, (prev) => {
      const p = partial && typeof partial === 'object' ? partial : {}
      const next = { ...prev }
      const ignored = []
      for (const [k, v] of Object.entries(p)) {
        if (!SETTABLE_FIELDS.has(k)) { ignored.push(k); continue }
        if (k === 'billing_status') {
          if (!BILLING_STATUSES.includes(v)) return fail('invalid', { key, field: k })
          next.billing_status = v
          if (!('billing_touched' in p)) next.billing_touched = true
        } else if (k === 'billing_touched' || k === 'rate_touched' || k === 'start_time_touched') {
          next[k] = v === true
        } else if (k === 'start_time') {
          const st = normalizeStartTime(typeof v === 'string' ? v.trim() : v)
          if (st == null) return fail('invalid', { key, field: k })
          next.start_time = st
          if (!('start_time_touched' in p)) next.start_time_touched = true
        } else if (k === 'rate') {
          const r = cleanRate(v)
          if (r == null) return fail('invalid', { key, field: k })
          next.rate = r
          if (!('rate_touched' in p)) next.rate_touched = r !== ''
        } else if (k === 'matter_phase_id') {
          const ph = cleanPhase(v)
          if (ph === undefined) return fail('invalid', { key, field: k })
          next.matter_phase_id = ph
        } else if (k === 'date') {
          const d = dateOnly(str(v))
          if (!d) return fail('invalid', { key, field: k })
          next.date = d
        } else {
          next[k] = str(v)
        }
      }
      return { ok: true, next, ignored }
    }, 'fields')
  }

  function lockedForMatterOrAttorney (key, prev) {
    if (prev.resume_entry_id != null && prev.resume_entry_id !== '') return fail('resume-locked', { key })
    if (isResumeSlotKey(key)) return fail('resume-locked', { key })
    if (prev.post_uncertain) return fail('post-uncertain', { key })
    return null
  }

  function setMatter (key, matter, { uid } = {}) {
    return merge(key, uid, (prev) => {
      const locked = lockedForMatterOrAttorney(key, prev)
      if (locked) return locked
      const m = matter || {}
      const id = cleanMatterId(m.matter_id)
      if (id == null) return fail('invalid', { key, field: 'matter_id' })
      const next = { ...prev, matter_id: id, matter_title: id ? str(m.matter_title) : '' }
      if (String(prev.matter_id ?? '') === id) return { ok: true, next }
      next.matter_phase_id = null
      if (!prev.billing_touched) {
        next.billing_status = deriveDefaultBillingStatus(id ? m.billing_type : null, E.firmDefaultBillingStatus())
      }
      if (!prev.rate_touched) next.rate = ''
      return { ok: true, next }
    }, 'matter')
  }

  function setAttorney (key, userId, { uid } = {}) {
    const u = resolveUid(uid)
    return merge(key, u, (prev) => {
      const locked = lockedForMatterOrAttorney(key, prev)
      if (locked) return locked
      const who = cleanUserId(userId)
      if (who == null) return fail('invalid', { key, field: 'user_id' })
      const value = who && who !== u ? who : ''
      const next = { ...prev, user_id: value }
      if (str(prev.user_id) === value) return { ok: true, next }
      if (!prev.rate_touched) next.rate = ''
      return { ok: true, next }
    }, 'attorney')
  }

  function setTypedHours (key, hours, { uid } = {}) {
    const u = resolveUid(uid)
    const t = clock()
    const L = loadV2(key, u, t)
    if (!L.ok) return L
    let prev = L.entry.slot
    if (isTicking(prev)) {
      // Shown paused because a later timer cut it (not reconciled yet):
      // materialise that pause in the same write.  Really ticking: refused.
      const entries = readAll(t)
      const cut = cutFor(L.entry, entries, t)
      if (cut >= t) return fail('ticking', { key })
      settleFrom(entries, u, t, key)
      prev = pausedClock(prev, cut)
    }
    const v = Number(hours)
    const next = { ...prev }
    if (hours === '' || hours == null || !Number.isFinite(v) || v <= 0) {
      if (next.hours_typed === undefined && prev === L.entry.slot) return { ok: true, key, changed: false, slot: prev }
      delete next.hours_typed
    } else {
      next.hours_typed = Math.round(v * 100) / 100
      next.pausedSeconds = Math.max(0, Math.round((next.hours_typed - 0.10) * 3600))
    }
    next.dirty = true
    const out = withSig(next)
    return written(key, commit(key, L.entry.slot, out, 'typed', t), out)
  }

  // ── save ───────────────────────────────────────────────────────────────
  // claimId → the tid it claimed, so finishSave recognises its own save even
  // when another window's stale claim replaced the claim id meanwhile (both
  // POSTs carry the same client_ref; the server replays the second).
  const claimTids = new Map()

  function claimForSave (key, { uid, formMatterId, at, ownershipConfirmed = false } = {}) {
    const u = resolveUid(uid)
    const t = clock()
    const L = loadForWrite(key, u, t)
    if (!L.ok) return L
    const e = L.entry
    const c = classifyEntry(e)
    if (c.cls === 'v1-held') return fail('held', { key })
    if (c.cls !== 'v2') return fail('needs-adopt', { key, cls: c.cls, probe: c.probe })
    const slot = e.slot
    if (claimIsFresh(slot.claim, t)) return fail('claimed', { key })
    if (formMatterId !== undefined && String(formMatterId ?? '') !== String(slot.matter_id ?? '')) {
      return fail('matter-mismatch', { key })
    }
    const isResume = slot.resume_entry_id != null && slot.resume_entry_id !== ''
    const entries = readAll(t)
    const ticking = isTicking(slot)
    const T = at != null ? clampCommandAt(slot, at, t, 'pause') : t
    const pausedSlot = ticking ? pausedClock(slot, Math.min(T, cutFor(e, entries, t))) : slot
    const seconds = baseSeconds(pausedSlot)
    const hours = slotSaveHours(pausedSlot, seconds)
    // The slot stops here: first persist the cuts it imposes on my earlier
    // ticking timers (settleFrom), or they would count its seconds again.
    const keepPaused = (reason, extra = {}) => {
      if (ticking) {
        settleFrom(entries, u, t, key)
        const w = commit(key, slot, pausedSlot, 'pause', t)
        if (w === false) return fail('storage', { key })
      }
      return fail(reason, { key, paused: true, seconds, hours, slot: pausedSlot, ...extra })
    }
    if (!isResume && !cleanMatterId(slot.matter_id)) return keepPaused('no-matter')
    if (!str(slot.description).trim()) return keepPaused('no-description')
    if (!(hours > 0)) return keepPaused('no-time')
    if (hours > MAX_ENTRY_HOURS) return keepPaused('too-long', { message: MESSAGES.tooLongHours(hours) })
    if (slot.uid_assumed && !ownershipConfirmed) return keepPaused('confirm-owner')
    const claimId = newUuid()
    const next = { ...pausedSlot, claim: { id: claimId, at: t } }
    if (slot.uid_assumed && ownershipConfirmed) delete next.uid_assumed
    const out = withSig(next)
    if (ticking) settleFrom(entries, u, t, key)
    const w = commit(key, slot, out, 'claim', t)
    if (w === false) return fail('storage', { key })
    if (claimTids.size > 100) claimTids.clear()
    claimTids.set(claimId, slot.tid)
    if (ownClaims.size > 100) ownClaims.clear()
    ownClaims.add(claimId)
    return {
      ok: true,
      key,
      changed: true,
      claimId,
      slot: out,
      hours,
      seconds,
      isResume,
      entryId: isResume ? Number(slot.resume_entry_id) : null,
      expectedUpdatedAt: slot.entry_updated_at != null ? slot.entry_updated_at : null,
      body: isResume ? buildPutBody(out) : buildPostBody(out, { hours })
    }
  }

  function loadClaimed (key, claimId, t) {
    const e = readEntry(key, t)
    if (e && e.error) return fail('storage', { key })
    if (!e || !e.slot) return { ok: true, missing: true }
    if (!e.slot.claim || e.slot.claim.id !== claimId) return fail('not-mine', { key })
    return { ok: true, entry: e }
  }

  // `error` (the failed request's error) applies postUncertainty's
  // tri-state; an explicit `uncertain` wins over it.
  function releaseClaim (key, claimId, { uncertain, error } = {}) {
    const t = clock()
    const L = loadClaimed(key, claimId, t)
    if (!L.ok) return L
    if (L.missing) return { ok: true, key, changed: false }
    const prev = L.entry.slot
    const next = { ...prev }
    delete next.claim
    const unsure = uncertain !== undefined ? uncertain : (error !== undefined ? postUncertainty(error) : undefined)
    if (unsure === true) next.post_uncertain = { at: t }
    else if (unsure === false) delete next.post_uncertain
    const out = withSig(next)
    return written(key, commit(key, prev, out, 'release', t), out)
  }

  function markReplayed (key, claimId, { entryId, entryUpdatedAt } = {}) {
    const t = clock()
    const id = Number(entryId)
    if (!Number.isInteger(id) || id <= 0) return fail('invalid', { key })
    const L = loadClaimed(key, claimId, t)
    if (!L.ok) return L
    if (L.missing) return fail('missing', { key })
    const prev = L.entry.slot
    const next = { ...prev, resume_entry_id: id, entry_updated_at: entryUpdatedAt != null ? entryUpdatedAt : null }
    // The follow-up PUT carries the start time this NEW entry was POSTed with
    // (its first start, or what the person typed): a resume slot's PUT sends
    // start_time only when touched.
    if (prev.start_time_touched !== true) {
      next.start_time = slotStartTime(prev)
      next.start_time_touched = true
    }
    delete next.post_uncertain
    const out = withSig(next)
    return written(key, commit(key, prev, out, 'replayed', t), out)
  }

  function finishSave (key, claimId, { broadcast: doBroadcast = true, tid } = {}) {
    if (!isTimerSlotKey(key)) return fail('invalid', { key })
    const t = clock()
    const e = readEntry(key, t)
    if (e && e.error) return fail('storage', { key })
    let removed = false
    const ourTid = tid || claimTids.get(claimId) || null
    claimTids.delete(claimId)
    if (e && e.slot) {
      const ours = (e.slot.claim && e.slot.claim.id === claimId) || (ourTid && e.slot.tid === ourTid)
      if (!ours) return fail('not-mine', { key })
      // A claimed slot is paused; one a stale window left ticking still cuts
      // my earlier timers — persist those cuts before it goes.
      if (isTicking(e.slot) && e.slot.uid != null && e.slot.uid !== '') settleFrom(readAll(t), String(e.slot.uid), t, key)
      if (!removeSlot(key, e.slot, 'saved', t)) return fail('storage', { key })
      removed = true
    } else {
      try { const s = S(); if (s) s.removeItem(ownerKeyFor(key)) } catch { /* best effort */ }
    }
    if (doBroadcast) sendBroadcast('saved', { key })
    return { ok: true, key, changed: removed }
  }

  function discard (key, { uid, force = false } = {}) {
    const u = resolveUid(uid)
    const t = clock()
    const L = loadForWrite(key, u, t)
    if (!L.ok) return L.reason === 'missing' ? { ok: true, key, changed: false } : L
    const e = L.entry
    const c = classifyEntry(e)
    if (c.cls === 'v1-held') return fail('held', { key, message: MESSAGES.discardInItsWindow })
    if (c.cls === 'v1-unknown' && !force) return fail('needs-adopt', { key, cls: c.cls, probe: c.probe })
    if (claimIsFresh(e.slot.claim, t)) return fail('claimed', { key })
    // A ticking slot cuts my earlier ticking timers at its start: persist
    // those cuts first, or removing it hands its seconds to them.
    if (isTicking(e.slot)) settleFrom(readAll(t), u, t, key)
    if (!removeSlot(key, e.slot, 'discard', t)) return fail('storage', { key })
    return { ok: true, key, changed: true }
  }

  function discardAllMine (uid) {
    const u = resolveUid(uid)
    if (u == null) return fail('no-user', { removed: [] })
    const t = clock()
    const removed = []
    let storageFailed = false
    for (const e of readAll(t)) {
      if (e.malformed || !e.slot || !e.v2 || String(e.slot.uid) !== u) continue
      if (claimIsFresh(e.slot.claim, t)) continue
      if (removeSlot(e.key, e.slot, 'discard', t)) removed.push(e.key)
      else storageFailed = true
    }
    return storageFailed ? fail('storage', { removed }) : { ok: true, removed }
  }

  // matter.blocked (firm decision 2026-10-13): keep the time — my v2 timers
  // on that matter pause (each at min(T, its cut)) and lose the matter
  // ('No matter yet'); resume slots on it are removed (PUT /:id could never
  // reach the entry).  A post_uncertain slot is cleared the same way and
  // loses post_uncertain: its retry can never reach the blocked matter, and
  // the server's client_ref lookup still refuses a second save (409) if the
  // first POST did land.  Claimed slots (a save in flight) are left alone.
  function blockMatter (matterId, { uid, at } = {}) {
    const u = resolveUid(uid)
    const id = cleanMatterId(matterId)
    const out = { ok: true, paused: [], cleared: [], removed: [] }
    if (u == null || !id) return out
    const t = clock()
    const entries = readAll(t)
    const targets = entries.filter(e => !e.malformed && e.slot && e.v2 && String(e.slot.uid) === u &&
      String(e.slot.matter_id ?? '') === id && !claimIsFresh(e.slot.claim, t) && classifyEntry(e).cls === 'v2')
    // These stop (or go): persist the cuts they impose on my other timers.
    settleFrom(entries, u, t, new Set(targets.map(e => e.key)))
    for (const e of targets) {
      const isResume = (e.slot.resume_entry_id != null && e.slot.resume_entry_id !== '') || isResumeSlotKey(e.key)
      if (isResume) {
        // The note first: a form open on it (here or in another window)
        // then says why it went, not "saved or discarded".
        writeNote(u, e.key, e.slot.tid, 'blocked', t)
        if (removeSlot(e.key, e.slot, 'blocked', t)) out.removed.push(e.key)
        else { dropNote(u, e.key, t); out.ok = false }
        continue
      }
      const ticking = isTicking(e.slot)
      const T = at != null ? clampCommandAt(e.slot, at, t, 'pause') : t
      let next = ticking ? pausedClock(e.slot, Math.min(T, cutFor(e, entries, t))) : { ...e.slot }
      next = { ...next, matter_id: '', matter_title: '', matter_phase_id: null, dirty: true }
      delete next.post_uncertain
      if (!e.slot.billing_touched) next.billing_status = deriveDefaultBillingStatus(null, E.firmDefaultBillingStatus())
      if (!e.slot.rate_touched) next.rate = ''
      const w = commit(e.key, e.slot, withSig(next), 'blocked', t)
      if (w === false) { out.ok = false; continue }
      if (ticking) out.paused.push(e.key)
      out.cleared.push(e.key)
    }
    if (!out.ok) return { ...fail('storage'), paused: out.paused, cleared: out.cleared, removed: out.removed }
    return out
  }

  // May an old tab or window still run this v1-unknown slot?  Only an
  // isPopupOpen probe that answers "closed" settles it.
  async function probeSaysClosed (c) {
    const probe = c && c.probe ? probeFn() : null
    if (!probe) return false
    try { return !(await probe(c.probe)) } catch { return false }
  }

  // Would adoptV1 ask before adopting `key`?  Never writes.  Discard asks
  // its own single confirm that includes the adopt question, then discards
  // with force — never two prompts for one click.
  async function adoptNeedsConfirm (key, { uid } = {}) {
    const L = loadForWrite(key, resolveUid(uid), clock())
    if (!L.ok) return false
    const c = classifyEntry(L.entry)
    if (!c || c.cls !== 'v1-unknown') return false
    return !(await probeSaysClosed(c))
  }

  async function adoptV1 (key, { uid, confirm } = {}) {
    const u = resolveUid(uid)
    const t0 = clock()
    const L = loadForWrite(key, u, t0)
    if (!L.ok) return L
    const c = classifyEntry(L.entry)
    if (c.cls === 'v2') return { ok: true, key, changed: false, adopted: false, slot: L.entry.slot }
    if (c.cls === 'v1-held') return fail('held', { key })
    if (c.cls === 'v1-unknown') {
      const silent = await probeSaysClosed(c)
      if (!silent) {
        const label = labelOf(L.entry.slot)
        if (typeof confirm !== 'function') return fail('needs-confirm', { key, kind: 'adopt', label })
        let yes = false
        try {
          yes = await confirm({ kind: 'adopt', key, label, danger: true, confirmLabel: 'Use it here', message: MESSAGES.adoptConfirm(label) })
        } catch { yes = false }
        if (yes !== true) return fail('cancelled', { key })
      }
    }
    // Fresh read after any await.
    const t = clock()
    const L2 = loadForWrite(key, u, t)
    if (!L2.ok) return L2
    const c2 = classifyEntry(L2.entry)
    if (c2.cls === 'v2') return { ok: true, key, changed: false, adopted: false, slot: L2.entry.slot }
    if (c2.cls === 'v1-held') return fail('held', { key })
    let next = adoptedSlot(L2.entry.slot, u, E.firmTz())
    let pausedOnAdopt = false
    if (isTicking(next)) {
      // From here the one-running rule applies to it.  If a later-started
      // timer of mine ticks, that one is the running timer: pause the adopted
      // one at the adoption instant with every second old code counted (no
      // cut reaching back into v1 time), never take over from the other.
      // Otherwise it keeps its own startTime and keeps ticking.
      const entries = readAll(t)
      if (cutFor({ key, slot: next }, entries, t) < t) {
        settleFrom(entries, u, t, key)
        next = pausedClock(next, t)
        pausedOnAdopt = true
      }
    }
    return written(key, commit(key, L2.entry.slot, next, 'adopt', t), next, { adopted: true, pausedOnAdopt })
  }

  function reconcile (uid, at) {
    const u = resolveUid(uid)
    if (u == null) return { ok: true, wrote: [] }
    const t = clock()
    const T = at != null ? Math.min(num(at, t), t) : t
    const r = settleFrom(readAll(t), u, t, null, T)
    return r.ok ? { ok: true, wrote: r.wrote } : { ...fail('storage'), wrote: r.wrote }
  }

  return {
    readSlot,
    getTimer,
    classify,
    listMyTimers,
    effectiveElapsedSeconds,
    startNewTimer,
    startOrResume,
    pause,
    toggle,
    pauseOrStart,
    seedResume,
    setFields,
    setMatter,
    setAttorney,
    setTypedHours,
    claimForSave,
    releaseClaim,
    markReplayed,
    finishSave,
    discard,
    discardAllMine,
    blockMatter,
    adoptV1,
    adoptNeedsConfirm,
    removalNote,
    reconcile,
    handleStorageEvent,
    billableHoursFromSeconds,
    buildPostBody,
    buildPutBody,
    formatElapsed: formatBarElapsed
  }
}

// The window-bound instance every surface uses.
export const timerStore = createTimerStore({
  storage: () => window.localStorage,
  now: () => Date.now(),
  dispatch: (detail) => {
    try { window.dispatchEvent(new CustomEvent(TIMER_SLOT_EVENT, { detail })) } catch { /* no window */ }
  },
  subscribe: (handler) => {
    if (typeof window === 'undefined') return () => {}
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  },
  broadcast: (type, payload) => broadcastTimerEvent(type, payload)
})

export default timerStore
